import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, CartesianGrid, Cell, Pie, PieChart, Tooltip, XAxis, YAxis } from "recharts";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Bot,
  BrainCircuit,
  Building2,
  CheckCircle2,
  ClipboardList,
  Database,
  Download,
  FileBarChart2,
  FileCheck2,
  FileSearch,
  Files,
  Filter,
  FolderOpen,
  Gauge,
  Globe2,
  LayoutDashboard,
  LogOut,
  Mail,
  MapPin,
  MessageSquareText,
  Phone,
  Radar,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  X,
} from "lucide-react";
import { toast, Toaster } from "sonner";

import "@/App.css";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_STORAGE_KEY = "dps360-session";
const PIE_COLORS = ["hsl(var(--tenant-primary))", "hsl(var(--tenant-accent))", "hsl(var(--chart-3))", "hsl(var(--chart-4))"];

const readStoredAuth = () => {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return { token: "", kind: "session", refreshToken: "" };
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.token) {
      return { token: parsed.token, kind: parsed.kind || "session", refreshToken: parsed.refreshToken || "" };
    }
  } catch {
    return { token: raw, kind: "session", refreshToken: "" };
  }
  return { token: "", kind: "session", refreshToken: "" };
};

const persistAuth = ({ token, kind, refreshToken = "" }) => {
  if (!token) {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ token, kind, refreshToken }));
};

const currencyFormatter = new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("id-ID");

const currentPathname = () => window.location.pathname;
const getPublicLeadTokenFromPath = () => {
  const path = currentPathname();
  return path.startsWith("/resources/lead/") ? path.split("/").pop() : "";
};

const resourceInterestOptions = [
  { value: "auto-default", label: "Belum memilih — kirim Manual Pengguna terlebih dahulu" },
  { value: "manual-pengguna", label: "Manual Pengguna Umum" },
  { value: "manual-ketua-dps", label: "Manual Ketua DPS" },
  { value: "manual-anggota-dps", label: "Manual Anggota DPS" },
  { value: "manual-sekretariat-dps", label: "Manual Sekretariat DPS" },
];


const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

const crmPipelineStages = [
  { value: "all", label: "Semua Pipeline" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "demo_scheduled", label: "Demo Scheduled" },
  { value: "proposal", label: "Proposal" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const crmLostReasons = [
  { value: "budget", label: "Budget" },
  { value: "timing", label: "Timing" },
  { value: "no_decision", label: "No decision" },
  { value: "competitor", label: "Competitor" },
  { value: "no_internal_priority", label: "No internal priority" },
  { value: "not_fit", label: "Not fit" },
  { value: "no_response", label: "No response" },
];

const apiHeaders = (token, kind = "session", extra = {}) => ({
  ...extra,
  ...(token ? (kind === "bearer" ? { Authorization: `Bearer ${token}` } : { "X-Session-Token": token }) : {}),
});

const metricTone = {
  success: "text-emerald-700 bg-emerald-50 border-emerald-200",
  warning: "text-amber-700 bg-amber-50 border-amber-200",
  danger: "text-rose-700 bg-rose-50 border-rose-200",
  info: "text-sky-700 bg-sky-50 border-sky-200",
};

const statusTone = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  submitted: "bg-sky-50 text-sky-700 border-sky-200",
  "dps review": "bg-teal-50 text-teal-700 border-teal-200",
  open: "bg-rose-50 text-rose-700 border-rose-200",
  remediation: "bg-amber-50 text-amber-700 border-amber-200",
  ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  scheduled: "bg-violet-50 text-violet-700 border-violet-200",
  "under review": "bg-teal-50 text-teal-700 border-teal-200",
  "approved with conditions": "bg-amber-50 text-amber-700 border-amber-200",
  trial: "bg-slate-100 text-slate-700 border-slate-200",
  past_due: "bg-rose-50 text-rose-700 border-rose-200",
};

const moduleIcons = {
  dashboard: LayoutDashboard,
  submissions: ClipboardList,
  opinions: FileCheck2,
  reviews: FileSearch,
  findings: Radar,
  meetings: Building2,
  documents: FolderOpen,
  knowledge: Database,
  reports: FileBarChart2,
  ai: BrainCircuit,
  notifications: Activity,
  audit: ShieldCheck,
  admin: Settings,
  tasks: Gauge,
  platform: Gauge,
  tenants: Building2,
  plans: Files,
  library: Globe2,
};

const brandAwareStyle = (tenant) => ({
  "--tenant-primary": tenant?.primary_color || "175 60% 28%",
  "--tenant-accent": tenant?.accent_color || "42 65% 52%",
});

const applyTenantBranding = (tenant) => {
  const root = document.documentElement;
  root.style.setProperty("--tenant-primary", tenant?.primary_color || "175 60% 28%");
  root.style.setProperty("--tenant-accent", tenant?.accent_color || "42 65% 52%");
};

const StatusBadge = ({ value }) => (
  <Badge
    variant="outline"
    className={cn(
      "rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em]",
      statusTone[value] || "bg-slate-100 text-slate-700 border-slate-200"
    )}
  >
    {String(value || "unknown").replaceAll("_", " ")}
  </Badge>
);

const MetricCard = ({ title, value, helper, tone = "info", testId }) => (
  <Card className="metric-sheen rounded-[18px] border-white/70 bg-white shadow-[var(--shadow-sm)]" data-testid={testId}>
    <CardHeader className="pb-3">
      <CardDescription className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{title}</CardDescription>
      <CardTitle className="text-3xl font-semibold tabular-nums">{value}</CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className={cn("inline-flex rounded-full border px-2.5 py-1 text-xs font-medium", metricTone[tone])}>{helper}</div>
    </CardContent>
  </Card>
);

const EmptyState = ({ title, description, action }) => (
  <Card className="rounded-[20px] border-dashed bg-white/80 text-center shadow-none">
    <CardContent className="flex min-h-[220px] flex-col items-center justify-center gap-3 p-8">
      <div className="rounded-full bg-[hsl(var(--tenant-primary))]/10 p-3 text-[hsl(var(--tenant-primary))]">
        <Sparkles className="h-6 w-6" />
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="max-w-md text-sm text-muted-foreground">{description}</p>
      {action}
    </CardContent>
  </Card>
);

const TopBarSearchResults = ({ results, onPick }) => {
  if (!results?.groups?.length) return null;
  return (
    <div className="search-results-popover absolute left-0 right-0 top-[calc(100%+8px)] z-40 rounded-2xl border bg-white p-3">
      <div className="space-y-3">
        {results.groups.map((group) => (
          <div key={group.label}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{group.label}</p>
            <div className="space-y-2">
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onPick(group.label)}
                  className="w-full rounded-xl border bg-slate-50 px-3 py-2 text-left transition-colors hover:bg-slate-100"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{item.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{item.snippet}</p>
                    </div>
                    {item.meta ? <StatusBadge value={item.meta} /> : null}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TableSection = ({ title, description, columns, rows, emptyMessage, testId, toolbar }) => (
  <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]" data-testid={testId}>
    <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
      <div>
        <CardTitle className="text-xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </div>
      {toolbar}
    </CardHeader>
    <CardContent className="pt-0">
      {rows?.length ? (
        <div className="rounded-2xl border">
          <Table>
            <TableHeader className="bg-slate-50/80">
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column.key} className="h-11 text-xs uppercase tracking-[0.1em]">
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className="dps-table-row">
                  {columns.map((column) => (
                    <TableCell key={`${row.id}-${column.key}`} className="align-top">
                      {column.render ? column.render(row) : row[column.key] || "—"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <EmptyState title="Belum ada data" description={emptyMessage} />
      )}
    </CardContent>
  </Card>
);

const FormField = ({ field, value, onChange }) => {
  if (field.type === "textarea") {
    return (
      <div className={field.span === 2 ? "md:col-span-2" : ""}>
        <label className="mb-2 block text-sm font-medium text-slate-700">{field.label}</label>
        <Textarea data-testid={field.testId} value={value || ""} onChange={(event) => onChange(field.name, event.target.value)} placeholder={field.placeholder} className="min-h-[96px] bg-white" />
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className={field.span === 2 ? "md:col-span-2" : ""}>
        <label className="mb-2 block text-sm font-medium text-slate-700">{field.label}</label>
        <Select value={value || field.options?.[0]?.value} onValueChange={(next) => onChange(field.name, next)}>
          <SelectTrigger data-testid={field.testId} className="bg-white">
            <SelectValue placeholder={field.placeholder || field.label} />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className={field.span === 2 ? "md:col-span-2" : ""}>
      <label className="mb-2 block text-sm font-medium text-slate-700">{field.label}</label>
      <Input
        data-testid={field.testId}
        type={field.type || "text"}
        value={value || ""}
        onChange={(event) => onChange(field.name, event.target.value)}
        placeholder={field.placeholder}
        className="bg-white"
      />
    </div>
  );
};

const ComposerCard = ({ title, description, fields, formState, setFormState, onSubmit, submitLabel, testId }) => (
  <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]" data-testid={testId}>
    <CardHeader>
      <CardTitle className="text-xl">{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="form-grid form-grid--two">
        {fields.map((field) => (
          <FormField key={field.name} field={field} value={formState[field.name]} onChange={(name, next) => setFormState((current) => ({ ...current, [name]: next }))} />
        ))}
      </div>
      <Button onClick={onSubmit} className="w-full rounded-xl" data-testid={`${testId}-submit`}>
        {submitLabel}
      </Button>
    </CardContent>
  </Card>
);


const ExitIntentPopup = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/45 px-4">
      <Card className="w-full max-w-xl rounded-[28px] border bg-white shadow-[var(--shadow-md)]">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-2xl">Sebelum Anda pergi…</CardTitle>
            <CardDescription>
              Unduh manual DPS360 Cloud sesuai minat Anda dan dapatkan akses aman untuk materi edukasi produk, use case, dan sesi konsultasi.
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" className="rounded-full" onClick={onClose} aria-label="Tutup popup">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border bg-slate-50/70 p-4 text-sm text-muted-foreground">
              <p className="font-semibold text-slate-900">Manual bertahap</p>
              <p className="mt-1">Materi dikirim bertahap sesuai minat dan respons calon customer.</p>
            </div>
            <div className="rounded-2xl border bg-slate-50/70 p-4 text-sm text-muted-foreground">
              <p className="font-semibold text-slate-900">Lead capture</p>
              <p className="mt-1">Form registrasi membantu mengumpulkan data potential customers secara rapi.</p>
            </div>
            <div className="rounded-2xl border bg-slate-50/70 p-4 text-sm text-muted-foreground">
              <p className="font-semibold text-slate-900">CTA lanjutan</p>
              <p className="mt-1">Alur berakhir pada CTA meeting & konsultasi di akhir nurturing.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild className="rounded-xl" data-testid="exit-intent-open-resources-button">
              <a href="/resources">Lihat Halaman Manual</a>
            </Button>
            <Button variant="outline" className="rounded-xl" onClick={onClose}>Lanjutkan ke demo</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const PublicResourcesScreen = ({
  catalog,
  lead,
  form,
  setForm,
  onRegister,
  registering,
  onDownload,
  onSelectManual,
  loading,
  onBackToDemo,
}) => {
  const isDownloadCenter = Boolean(lead);

  if (loading && !catalog) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--background))] px-4">
        <Card className="w-full max-w-lg rounded-[26px] border bg-white shadow-[var(--shadow-sm)]">
          <CardContent className="flex items-center gap-4 p-6">
            <div className="h-12 w-12 animate-pulse rounded-2xl bg-[hsl(var(--tenant-primary))]/15" />
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[hsl(var(--tenant-primary))]">DPS360 Resources</p>
              <p className="text-lg font-semibold text-slate-900">Menyiapkan landing page dan download center...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] space-y-6">
        <Card className="overflow-hidden rounded-[30px] border bg-white shadow-[var(--shadow-md)]">
          <div className="tenant-band" />
          <CardContent className="grid gap-8 p-8 lg:grid-cols-[1.05fr_0.95fr] lg:p-12">
            <div className="space-y-6">
              <div className="inline-flex items-center rounded-full border border-[hsl(var(--tenant-primary))]/20 bg-[hsl(var(--tenant-primary))]/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--tenant-primary))]">
                Resource Center & Lead Capture
              </div>
              <div>
                <h1 className="text-balance text-4xl font-semibold leading-tight text-slate-950 md:text-5xl">
                  Unduh manual DPS360 Cloud sesuai minat Anda dan buka jalur menuju demo serta konsultasi.
                </h1>
                <p className="mt-4 max-w-3xl text-base leading-8 text-muted-foreground">
                  Halaman ini dirancang untuk menjelaskan kapabilitas DPS360 Cloud, mengumpulkan data calon customer, dan memberi akses download aman ke manual yang relevan berdasarkan minat atau jabatan.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {(catalog?.feature_sections || []).map((feature) => (
                  <div key={feature.title} className="rounded-[24px] border bg-slate-50/70 p-5">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-[hsl(var(--tenant-primary))]/10 text-[hsl(var(--tenant-primary))]">
                      <Target className="h-5 w-5" />
                    </div>
                    <p className="font-semibold text-slate-900">{feature.title}</p>
                    <p className="mt-2 text-sm leading-7 text-muted-foreground">{feature.description}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild className="rounded-xl" data-testid="resources-cta-demo-button">
                  <a href="/">Kembali ke Demo</a>
                </Button>
                <Button asChild variant="outline" className="rounded-xl" data-testid="resources-cta-meeting-button">
                  <a href={lead?.meeting_cta_url || "mailto:info@pakdeheru.com?subject=Permintaan%20Meeting%20DPS360%20Cloud"}>CTA Meeting & Konsultasi</a>
                </Button>
              </div>
            </div>

            <div className="space-y-6">
              <Card className="rounded-[28px] border bg-slate-50/80 shadow-none">
                <CardHeader>
                  <CardTitle className="text-2xl">{isDownloadCenter ? `Download Center untuk ${lead.full_name}` : "Form registrasi akses manual"}</CardTitle>
                  <CardDescription>
                    {isDownloadCenter
                      ? "Akses download aman Anda sudah aktif. Manual berikutnya akan ditawarkan bertahap sesuai minat dan respons nurturing."
                      : "Isi form untuk membuka akses download aman. Jika belum memilih minat, sistem akan memberikan Buku Manual Pengguna terlebih dahulu."}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!isDownloadCenter ? (
                    <>
                      <div className="form-grid form-grid--two">
                        <FormField field={{ name: "full_name", label: "Nama Lengkap", testId: "resources-full-name-input", placeholder: "Nama Anda" }} value={form.full_name} onChange={(name, value) => setForm((current) => ({ ...current, [name]: value }))} />
                        <FormField field={{ name: "email", label: "Email", type: "email", testId: "resources-email-input", placeholder: "nama@institusi.com" }} value={form.email} onChange={(name, value) => setForm((current) => ({ ...current, [name]: value }))} />
                        <FormField field={{ name: "institution", label: "Institusi", testId: "resources-institution-input", placeholder: "Nama institusi" }} value={form.institution} onChange={(name, value) => setForm((current) => ({ ...current, [name]: value }))} />
                        <FormField field={{ name: "job_title", label: "Jabatan", testId: "resources-job-title-input", placeholder: "Ketua DPS / Sekretariat / Compliance" }} value={form.job_title} onChange={(name, value) => setForm((current) => ({ ...current, [name]: value }))} />
                        <FormField field={{ name: "whatsapp", label: "No. WhatsApp", testId: "resources-whatsapp-input", placeholder: "08xxxxxxxxxx" }} value={form.whatsapp} onChange={(name, value) => setForm((current) => ({ ...current, [name]: value }))} />
                        <FormField field={{ name: "city", label: "Kota", testId: "resources-city-input", placeholder: "Jakarta" }} value={form.city} onChange={(name, value) => setForm((current) => ({ ...current, [name]: value }))} />
                      </div>
                      <div>
                        <label className="mb-3 block text-sm font-medium text-slate-700">Manual yang paling diminati</label>
                        <div className="grid gap-2">
                          {resourceInterestOptions.map((option) => {
                            const active = form.interest_manual_key === option.value;
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => setForm((current) => ({ ...current, interest_manual_key: option.value }))}
                                data-testid={option.value === 'auto-default' ? 'resources-interest-default' : `resources-interest-${option.value}`}
                                className={cn(
                                  "rounded-2xl border px-4 py-3 text-left text-sm transition-colors",
                                  active ? "border-[hsl(var(--tenant-primary))] bg-[hsl(var(--tenant-primary))]/5 text-slate-900" : "bg-white hover:bg-slate-50"
                                )}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        <p className="font-semibold">Status email saat ini</p>
                        <p className="mt-1">{catalog?.email_delivery_note || "Status email sedang disiapkan."}</p>
                      </div>
                      <Button onClick={onRegister} disabled={registering} className="w-full rounded-xl" data-testid="resources-register-submit-button">
                        {registering ? "Menyimpan data..." : "Buka Akses Download"}
                      </Button>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div className="rounded-2xl border bg-white p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{lead.institution}</p>
                            <p className="text-sm text-muted-foreground">{lead.job_title} · {lead.city}</p>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Mail className="h-4 w-4" /> {lead.email}
                          </div>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        <p className="font-semibold">Status email konfirmasi</p>
                        <p className="mt-1">{lead.email_delivery_note}</p>
                      </div>
                      <div className="grid gap-3">
                        {lead.granted_manuals.map((manual) => (
                          <div key={manual.key} className="rounded-2xl border bg-white p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="font-semibold text-slate-900">{manual.title}</p>
                                <p className="mt-1 text-sm text-muted-foreground">{manual.audience}</p>
                                <p className="mt-3 text-sm leading-7 text-muted-foreground">{manual.summary}</p>
                              </div>
                              <Button className="rounded-xl" onClick={() => onDownload(manual.key)} data-testid={`resources-download-${manual.key}`}>
                                <Download className="mr-2 h-4 w-4" /> Download
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
          <Card className="rounded-[28px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <CardTitle className="text-2xl">Manual yang tersedia</CardTitle>
              <CardDescription>Manual disusun per peran agar proses nurturing relevan dengan jabatan dan kebutuhan calon customer.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              {(catalog?.manuals || []).map((manual) => (
                <div key={manual.key} className="rounded-[24px] border bg-slate-50/70 p-5">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(var(--tenant-primary))]/10 text-[hsl(var(--tenant-primary))]">
                    <BookOpen className="h-5 w-5" />
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-slate-900">{manual.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{manual.audience}</p>
                    </div>
                    {lead?.downloaded_manual_keys?.includes(manual.key) ? <Badge className="rounded-full bg-emerald-600 text-white">Downloaded</Badge> : null}
                  </div>
                  <p className="mt-3 text-sm leading-7 text-muted-foreground">{manual.summary}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {manual.topics.map((topic) => (
                      <Badge key={topic} variant="outline" className="rounded-full border-slate-200 bg-white text-slate-700">{topic}</Badge>
                    ))}
                  </div>
                  {lead && !lead.granted_manuals.find((item) => item.key === manual.key) ? (
                    <Button variant="outline" className="mt-5 rounded-xl" onClick={() => onSelectManual(manual.key)} data-testid={`resources-select-${manual.key}`}>
                      Tandai Minat untuk Nurturing
                    </Button>
                  ) : null}
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="rounded-[28px] border bg-white shadow-[var(--shadow-sm)]">
              <CardHeader>
                <CardTitle className="text-2xl">Alur nurturing</CardTitle>
                <CardDescription>File dikirim bertahap berdasarkan minat awal dan respons user terhadap nurturing berikutnya.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {(lead?.nurture_schedule || [
                  { day: 0, notes: "Manual pertama dikirim sesuai minat atau default Manual Pengguna." },
                  { day: 2, notes: "Tawarkan manual lain yang belum didownload." },
                  { day: 5, notes: "Follow-up penawaran manual lain yang belum didownload." },
                  { day: 7, notes: "Follow-up penawaran manual lain yang belum didownload." },
                  { day: 9, notes: "Follow-up penawaran manual lain yang belum didownload." },
                  { day: 10, notes: "CTA meeting dan konsultasi." },
                ]).map((step) => (
                  <div key={`${step.day}-${step.type || step.notes}`} className="rounded-2xl border bg-slate-50/70 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-semibold text-slate-900">Hari {step.day}</p>
                      {step.status ? <Badge variant="outline" className="rounded-full">{step.status.replaceAll("_", " ")}</Badge> : null}
                    </div>
                    <p className="mt-2 text-sm leading-7 text-muted-foreground">{step.notes}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="rounded-[28px] border bg-white shadow-[var(--shadow-sm)]">
              <CardHeader>
                <CardTitle className="text-2xl">CTA lanjutan</CardTitle>
                <CardDescription>Gunakan lead data ini untuk mendorong demo, meeting, dan konsultasi produk.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div className="rounded-2xl border bg-slate-50/70 p-4">
                  <div className="mb-2 flex items-center gap-2 text-slate-900"><Phone className="h-4 w-4" /> CTA Meeting</div>
                  <p>Hari ke-10 diarahkan untuk mengajak calon customer ke sesi meeting dan konsultasi implementasi DPS360 Cloud.</p>
                </div>
                <div className="rounded-2xl border bg-slate-50/70 p-4">
                  <div className="mb-2 flex items-center gap-2 text-slate-900"><MapPin className="h-4 w-4" /> Data Potential Customers</div>
                  <p>Nama, email, institusi, jabatan, WhatsApp, kota, dan minat manual disimpan untuk mempermudah follow-up sales dan customer education.</p>
                </div>
                <Button variant="outline" className="w-full rounded-xl" onClick={onBackToDemo}>Kembali ke halaman demo</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

const CrmPortalLoginScreen = ({ form, setForm, onLogin, loading }) => (
  <div className="min-h-screen bg-[hsl(var(--background))] px-4 py-6 sm:px-6 lg:px-8">
    <div className="mx-auto grid min-h-[calc(100vh-3rem)] max-w-[1440px] overflow-hidden rounded-[32px] border bg-white shadow-[var(--shadow-md)] lg:grid-cols-[1fr_1fr]">
      <div className="p-10 lg:p-14">
        <div className="mb-10 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-lg font-bold text-white">CRM</div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">admin-crm.dps-360.com</p>
            <h1 className="text-2xl font-semibold text-slate-900">DPS360 Sales CRM</h1>
          </div>
        </div>
        <div className="max-w-xl space-y-4">
          <p className="inline-flex items-center rounded-full border border-[hsl(var(--tenant-primary))]/20 bg-[hsl(var(--tenant-primary))]/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--tenant-primary))]">Portal penjualan terpisah</p>
          <h2 className="text-4xl font-semibold leading-tight text-slate-950">Akses CRM khusus untuk monitoring lead, nurturing, dan penjualan DPS360.</h2>
          <p className="text-base leading-8 text-muted-foreground">Portal ini dipisahkan secara logical dari website utama dps-360.com dan difokuskan untuk investor/white-label pipeline, follow-up, demo scheduling, dan email activity.</p>
        </div>
        <div className="mt-8 rounded-[28px] border bg-slate-50/70 p-6">
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Email CRM Admin</label>
              <Input value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} className="bg-white" data-testid="crm-portal-email-input" />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Password</label>
              <Input type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} className="bg-white" data-testid="crm-portal-password-input" />
            </div>
            <Button className="w-full rounded-xl" onClick={onLogin} disabled={loading} data-testid="crm-portal-login-button">{loading ? 'Memuat...' : 'Masuk ke CRM'}</Button>
            <Button asChild variant="outline" className="w-full rounded-xl"><a href="/">Kembali ke website utama</a></Button>
          </div>
        </div>
      </div>
      <div className="dps-login-hero noise-overlay relative hidden lg:block">
        <div className="absolute inset-0 bg-slate-950/40" />
        <div className="relative z-10 flex h-full flex-col justify-between p-10 text-white">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm"><p className="text-xs uppercase tracking-[0.14em] text-white/65">Lead Intelligence</p><p className="mt-2 text-2xl font-semibold">CRM</p><p className="mt-1 text-sm text-white/70">Pipeline, filters, kanban, exports</p></div>
            <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm"><p className="text-xs uppercase tracking-[0.14em] text-white/65">Email Ops</p><p className="mt-2 text-2xl font-semibold">SMTP</p><p className="mt-1 text-sm text-white/70">Resend, retry, nurture history</p></div>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur-sm">
            <p className="text-sm uppercase tracking-[0.16em] text-white/70">admin-crm portal</p>
            <h3 className="mt-3 text-3xl font-semibold">Tidak tampil di website utama, tetapi siap untuk domain terpisah.</h3>
          </div>
        </div>
      </div>
      <Toaster richColors position="top-right" />
    </div>
  </div>
);

const LoginScreen = ({
  bootstrap,
  form,
  setForm,
  onDemoLogin,
  loading,
  localLoginForm,
  setLocalLoginForm,
  onLocalLogin,
  localLoading,
}) => {
  const tenantRoles = bootstrap?.tenant_roles || [];
  const selectedTenant = bootstrap?.tenants?.find((tenant) => tenant.id === form.tenant_id);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto grid min-h-[calc(100vh-3rem)] max-w-[1440px] overflow-hidden rounded-[32px] border bg-white shadow-[var(--shadow-md)] lg:grid-cols-[1.05fr_0.95fr]">
        <div className="relative flex flex-col justify-between p-8 sm:p-10 lg:p-14">
          <div>
            <div className="mb-10 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(var(--tenant-primary))] text-lg font-bold text-white">DPS</div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[hsl(var(--tenant-primary))]">Multi-Tenant Cloud</p>
                <h1 className="text-2xl font-semibold text-slate-900">DPS360 Cloud</h1>
              </div>
            </div>
            <div className="max-w-xl">
              <p className="mb-3 inline-flex items-center rounded-full border border-[hsl(var(--tenant-primary))]/20 bg-[hsl(var(--tenant-primary))]/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--tenant-primary))]">
                Premium vertical SaaS untuk pengawasan syariah
              </p>
              <h2 className="text-balance text-4xl font-semibold leading-tight text-slate-950 md:text-5xl">Masuk ke operating system DPS yang siap untuk demo investor dan enterprise buyer.</h2>
              <p className="mt-4 text-base leading-7 text-muted-foreground">
                Gunakan Demo Access untuk alur cepat, atau masuk dengan email/password berbasis JWT untuk Phase 4 auth upgrade sambil tetap mempertahankan opsi SSO-ready placeholder.
              </p>
            </div>
          </div>

          <Card className="rounded-[28px] border bg-slate-50/70 shadow-none">
            <CardHeader>
              <CardTitle className="text-xl">Masuk ke workspace</CardTitle>
              <CardDescription>Pilih mode login sesuai kebutuhan: demo tenant-aware atau login email/password dengan JWT.</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="demo" className="space-y-4">
                <TabsList className="w-full rounded-2xl border bg-white p-1">
                  <TabsTrigger value="demo" className="flex-1">Demo Access</TabsTrigger>
                  <TabsTrigger value="local" className="flex-1">Email & Password</TabsTrigger>
                </TabsList>
                <TabsContent value="demo" className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Mode workspace</label>
                    <Select value={form.workspace_type} onValueChange={(value) => setForm((current) => ({ ...current, workspace_type: value }))}>
                      <SelectTrigger data-testid="login-workspace-select" className="bg-white">
                        <SelectValue placeholder="Pilih mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {bootstrap?.workspace_types?.map((item) => (
                          <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.workspace_type === "tenant" ? (
                    <>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Tenant</label>
                        <Select value={form.tenant_id} onValueChange={(value) => setForm((current) => ({ ...current, tenant_id: value }))}>
                          <SelectTrigger data-testid="login-tenant-select" className="bg-white">
                            <SelectValue placeholder="Pilih tenant demo" />
                          </SelectTrigger>
                          <SelectContent>
                            {bootstrap?.tenants?.map((tenant) => (
                              <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">Peran demo</label>
                        <Select value={form.role_key} onValueChange={(value) => setForm((current) => ({ ...current, role_key: value }))}>
                          <SelectTrigger data-testid="login-role-select" className="bg-white">
                            <SelectValue placeholder="Pilih peran" />
                          </SelectTrigger>
                          <SelectContent>
                            {tenantRoles.map((role) => (
                              <SelectItem key={role.key} value={role.key}>{role.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  ) : null}
                  {selectedTenant ? (
                    <div className="rounded-2xl border bg-white p-4" style={brandAwareStyle(selectedTenant)}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-[hsl(var(--tenant-primary))]">{selectedTenant.name}</p>
                          <p className="mt-1 text-sm text-muted-foreground">{selectedTenant.login_headline}</p>
                        </div>
                        <StatusBadge value={selectedTenant.plan_id} />
                      </div>
                    </div>
                  ) : null}
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Button onClick={onDemoLogin} disabled={loading} className="rounded-xl" data-testid="login-demo-button">
                      {loading ? "Memuat..." : "Masuk Demo"}
                    </Button>
                    <Button asChild variant="outline" className="rounded-xl" data-testid="login-open-resources-button">
                      <a href="/resources">Lihat Manual</a>
                    </Button>
                    <Button variant="outline" disabled className="rounded-xl" data-testid="login-sso-placeholder-button" title="SSO ready placeholder">
                      Masuk dengan SSO
                    </Button>
                  </div>
                </TabsContent>
                <TabsContent value="local" className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Email</label>
                    <Input data-testid="login-email-input" value={localLoginForm.email} onChange={(event) => setLocalLoginForm((current) => ({ ...current, email: event.target.value }))} placeholder="tenant_admin@bank-syariah-amanah.demo" className="bg-white" />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Password</label>
                    <Input data-testid="login-password-input" type="password" value={localLoginForm.password} onChange={(event) => setLocalLoginForm((current) => ({ ...current, password: event.target.value }))} placeholder="Amanah123!" className="bg-white" />
                  </div>
                  <Button onClick={onLocalLogin} disabled={localLoading} className="w-full rounded-xl" data-testid="login-local-button">
                    {localLoading ? "Memuat..." : "Masuk dengan Email"}
                  </Button>
                  <div className="rounded-2xl border bg-white p-4 text-sm text-muted-foreground">
                    <p className="font-semibold text-slate-900">Contoh kredensial Phase 4</p>
                    <div className="mt-3 space-y-2">
                      {(bootstrap?.local_auth_examples || []).map((example) => (
                        <button
                          key={example.email}
                          type="button"
                          onClick={() => setLocalLoginForm({ email: example.email, password: example.password_hint })}
                          className="w-full rounded-xl border bg-slate-50 px-3 py-2 text-left hover:bg-slate-100"
                        >
                          <p className="font-medium text-slate-900">{example.label}</p>
                          <p className="text-xs text-muted-foreground">{example.email} · {example.password_hint}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="mt-4 grid gap-2 pt-2 text-sm text-muted-foreground sm:grid-cols-3">
                <div className="rounded-2xl border bg-white p-3">
                  <p className="font-semibold text-slate-900">Tenant-aware</p>
                  <p className="mt-1">Data, AI, dokumen, dan audit log terisolasi ketat per tenant.</p>
                </div>
                <div className="rounded-2xl border bg-white p-3">
                  <p className="font-semibold text-slate-900">JWT-ready</p>
                  <p className="mt-1">Auth lokal berbasis access token dan refresh token untuk Phase 4.</p>
                </div>
                <div className="rounded-2xl border bg-white p-3">
                  <p className="font-semibold text-slate-900">AI with citations</p>
                  <p className="mt-1">Output AI selalu dilabeli Draft AI dan disertai sumber dokumen.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="dps-login-hero noise-overlay relative hidden overflow-hidden p-10 text-white lg:flex lg:flex-col lg:justify-between">
          <div className="dps-grid-pattern absolute inset-0 opacity-20" />
          <div className="relative z-10 flex items-center justify-between">
            <Badge className="rounded-full border-white/20 bg-white/10 px-3 py-1 text-white">{bootstrap?.product?.name}</Badge>
            <Badge className="rounded-full border-white/20 bg-white/10 px-3 py-1 text-white">Commercial SaaS</Badge>
          </div>
          <div className="relative z-10 max-w-xl space-y-6">
            <div className="space-y-3">
              <p className="text-sm uppercase tracking-[0.18em] text-white/70">Boardroom-grade executive experience</p>
              <h2 className="text-4xl font-semibold leading-tight">Satu command center untuk opini, temuan, rapat, evidence, dan kontrol governance lintas tenant.</h2>
              <p className="max-w-lg text-base leading-7 text-white/78">
                Phase 3 menghadirkan komersialisasi yang lebih kuat, sedangkan Phase 4 menambahkan email/password auth berbasis JWT tanpa mengorbankan demo login untuk testing dan investor demo.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {(bootstrap?.tenants || []).slice(0, 4).map((tenant) => (
                <div key={tenant.id} className="rounded-[24px] border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 text-sm font-bold">{tenant.logo_text || tenant.name?.slice(0, 2)}</div>
                    <StatusBadge value={tenant.plan_id} />
                  </div>
                  <p className="text-sm font-semibold text-white">{tenant.name}</p>
                  <p className="mt-1 text-sm text-white/70">{tenant.industry}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative z-10 grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.14em] text-white/65">Multi-tenant</p>
              <p className="mt-2 text-2xl font-semibold">4</p>
              <p className="mt-1 text-sm text-white/70">tenant demo dengan identitas brand berbeda</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.14em] text-white/65">AI Agent</p>
              <p className="mt-2 text-2xl font-semibold">7</p>
              <p className="mt-1 text-sm text-white/70">mode agent tenant-aware dengan citation</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
              <p className="text-xs uppercase tracking-[0.14em] text-white/65">JWT Auth</p>
              <p className="mt-2 text-2xl font-semibold">Live</p>
              <p className="mt-1 text-sm text-white/70">email/password + refresh token untuk akses non-demo</p>
            </div>
          </div>
        </div>
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
};

function App() {
  const initialAuth = readStoredAuth();
  const [bootstrap, setBootstrap] = useState(null);
  const [sessionToken, setSessionToken] = useState(initialAuth.token);
  const [tokenKind, setTokenKind] = useState(initialAuth.kind);
  const [crmPortalLoginForm, setCrmPortalLoginForm] = useState({
    email: "herusidik@gmail.com",
    password: "D10891heru",
  });

  const [refreshToken, setRefreshToken] = useState(initialAuth.refreshToken);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [crmPortalAuthenticated, setCrmPortalAuthenticated] = useState(false);
  const [crmPortalUser, setCrmPortalUser] = useState(null);

  const [localLoggingIn, setLocalLoggingIn] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [documentPreview, setDocumentPreview] = useState({ doc: null, url: "" });
  const [aiResult, setAiResult] = useState(null);
  const [aiRunning, setAiRunning] = useState(false);
  const [dashboardSummary, setDashboardSummary] = useState("");

  const [loginForm, setLoginForm] = useState({
    workspace_type: "tenant",
    tenant_id: "bank-syariah-amanah",
    role_key: "tenant_admin",
  });

  const [tenantForm, setTenantForm] = useState({
    name: "",
    industry: "Bank Umum Syariah",
    plan_id: "professional",
    primary_color: "175 60% 28%",
    accent_color: "42 65% 52%",
    login_headline: "Workspace DPS baru siap diaktifkan.",
  });
  const [crmFilters, setCrmFilters] = useState({
    pipeline_stage: "all",
    owner_name: "all",
    city: "all",
    priority: "all",
    source: "all",
    search: "",
  });
  const [crmViewMode, setCrmViewMode] = useState("table");

  const [crmOverview, setCrmOverview] = useState(null);
  const [crmLeads, setCrmLeads] = useState([]);
  const [crmSelectedLeadId, setCrmSelectedLeadId] = useState("");
  const [crmLeadDetail, setCrmLeadDetail] = useState(null);
  const [crmLoading, setCrmLoading] = useState(false);
  const [crmLeadUpdateForm, setCrmLeadUpdateForm] = useState({
    pipeline_stage: "new",
    owner_name: "Superadmin-CRM",
    lead_score: 35,
    qualification_note: "",
    next_action_date: "",
    priority: "medium",
    lost_reason: "budget",
  });
  const [crmNoteForm, setCrmNoteForm] = useState({ note: "", author: "Superadmin-CRM" });
  const [crmFollowupForm, setCrmFollowupForm] = useState({ title: "", due_date: "", owner_name: "Superadmin-CRM", channel: "whatsapp" });
  const [crmMeetingForm, setCrmMeetingForm] = useState({ title: "Demo DPS360 Cloud", scheduled_for: "", meeting_link: "", notes: "" });


  const [localLoginForm, setLocalLoginForm] = useState({
    email: "tenant_admin@bank-syariah-amanah.demo",
    password: "Amanah123!",
  });

  const [submissionForm, setSubmissionForm] = useState({
    title: "",
    request_type: "new_product",
    requester: "",
    description: "",
    business_unit: "Pembiayaan Konsumer",
    branch: "Cabang Jakarta",
    due_date: "",
  });
  const [opinionForm, setOpinionForm] = useState({ title: "", issue_type: "new_product", background: "", final_stance: "Draft", conditions: "" });
  const [findingForm, setFindingForm] = useState({ title: "", source: "Internal Audit", category: "documentation", severity: "High", owner: "", description: "", target_date: "" });
  const [meetingForm, setMeetingForm] = useState({ title: "", meeting_date: "", location: "Board Room Hybrid", agenda_text: "" });
  const [knowledgeForm, setKnowledgeForm] = useState({ title: "", category: "internal_policy", content: "", tags: "" });
  const [brandingForm, setBrandingForm] = useState({ organization_name: "", primary_color: "", accent_color: "", login_headline: "", report_header: "", report_footer: "" });
  const [aiSettingsForm, setAiSettingsForm] = useState({ prompt_retention: "180 hari", export_enabled: true });
  const [documentUpload, setDocumentUpload] = useState({ title: "", document_class: "internal_policy", sensitivity: "internal", unit: "Sekretariat DPS", tags: "", file: null });
  const [aiComposer, setAiComposer] = useState({ agent_name: "Ask DPS Copilot", question: "", doc_ids: [] });
  const [resourceCatalog, setResourceCatalog] = useState(null);
  const [resourceLead, setResourceLead] = useState(null);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceSubmitting, setResourceSubmitting] = useState(false);
  const [exitIntentVisible, setExitIntentVisible] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    full_name: "",
    email: "",
    institution: "",
    job_title: "",
    whatsapp: "",
    city: "",
    interest_manual_key: "auto-default",
  });


  const activePath = currentPathname();
  const isPublicResourcesRoute = activePath === "/resources" || activePath.startsWith("/resources/lead/");
  const isCrmPortalRoute = activePath.startsWith("/admin-crm") || window.location.hostname.startsWith("admin-crm.");
  const publicLeadToken = getPublicLeadTokenFromPath();

  const fetchBootstrap = async () => {
    const response = await axios.get(`${API}/bootstrap`, { headers: apiHeaders(sessionToken, tokenKind) });
    setBootstrap(response.data);
  };

  const fetchWorkspace = async (token = sessionToken, kind = tokenKind) => {
    if (!token) {
      setWorkspace(null);
      return;
    }
    let payload;
    if (kind === "bearer" && isCrmPortalRoute) {
      const response = await fetch(`${API}/session/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`CRM portal session failed with status ${response.status}`);
      }
      payload = await response.json();
    } else {
      const response = await axios.get(`${API}/session/me`, { headers: apiHeaders(token, kind) });
      payload = response.data;
    }
    setWorkspace(payload);
    setCurrentPage((current) => current || payload.default_page);
    if (payload.workspace_type === "tenant") {
      const tenant = payload.data.tenant;
      setBrandingForm({
        organization_name: tenant.name || "",
        primary_color: tenant.primary_color || "",
        accent_color: tenant.accent_color || "",
        login_headline: tenant.login_headline || "",
        report_header: tenant.report_header || "",
        report_footer: tenant.report_footer || "",
      });
      applyTenantBranding(tenant);
      setAiComposer((current) => ({
        ...current,
        doc_ids: payload.data.documents.slice(0, 2).map((item) => item.id),
      }));
      setAiSettingsForm({
        prompt_retention: payload.data.ai_settings.prompt_retention,
        export_enabled: payload.data.ai_settings.export_enabled,
      });
    } else {
      applyTenantBranding(null);
    }
  };

  useEffect(() => {
    if (!isPublicResourcesRoute) return;
    const loadPublicResources = async () => {
      setResourceLoading(true);
      try {
        const [catalogResponse, leadResponse] = await Promise.all([
          axios.get(`${API}/public/resources/catalog`),
          publicLeadToken ? axios.get(`${API}/public/resources/lead/${publicLeadToken}`) : Promise.resolve(null),
        ]);
        setResourceCatalog(catalogResponse.data);

  const loadWorkspaceWithRetry = async (token = sessionToken, kind = tokenKind, maxAttempts = 5) => {
    let lastError = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        await fetchWorkspace(token, kind);
        return true;
      } catch (error) {
        lastError = error;
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 1200));
        }
      }
    }
    if (lastError) throw lastError;
    return false;
  };

        setResourceLead(leadResponse?.data?.lead || null);
      } catch (error) {
        console.error(error);
        toast.error("Gagal memuat halaman resources");
      } finally {
        setResourceLoading(false);
      }
    };
    loadPublicResources();
  }, [isPublicResourcesRoute, publicLeadToken]);

  useEffect(() => {
    if ((workspace?.workspace_type === "super_admin" && currentPage === "crm") || (isCrmPortalRoute && crmPortalAuthenticated && currentPage === "crm")) {
      fetchCrmOverview();
    }
  }, [workspace, currentPage, isCrmPortalRoute, crmPortalAuthenticated]);


  useEffect(() => {
    if (loading || workspace || isPublicResourcesRoute) return undefined;
    const shouldOpen = () => {
      if (!sessionStorage.getItem("dps360-exit-popup-shown")) {
        setExitIntentVisible(true);
        sessionStorage.setItem("dps360-exit-popup-shown", "true");
      }
    };
    const handleMouseLeave = (event) => {
      if (event.clientY <= 10) shouldOpen();
    };
    const handleMouseMove = (event) => {
      if (event.clientY <= 6) shouldOpen();
    };
    document.addEventListener("mouseout", handleMouseLeave);
    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mouseout", handleMouseLeave);
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [loading, workspace, isPublicResourcesRoute]);

  const filteredCrmLeads = useMemo(() => {
    return crmLeads.filter((lead) => {
      if (crmFilters.pipeline_stage !== "all" && lead.pipeline_stage !== crmFilters.pipeline_stage) return false;
      if (crmFilters.owner_name !== "all" && (lead.owner_name || "Unassigned") !== crmFilters.owner_name) return false;
      if (crmFilters.city !== "all" && lead.city !== crmFilters.city) return false;
      if (crmFilters.priority !== "all" && lead.priority !== crmFilters.priority) return false;
      if (crmFilters.source !== "all" && lead.lead_source !== crmFilters.source) return false;
      if (crmFilters.search.trim()) {
        const haystack = [lead.full_name, lead.email, lead.institution, lead.job_title].join(" ").toLowerCase();
        if (!haystack.includes(crmFilters.search.toLowerCase())) return false;
      }
      return true;
    });
  }, [crmLeads, crmFilters]);

  const crmOwnerOptions = useMemo(() => ["all", ...new Set(crmLeads.map((lead) => lead.owner_name || "Unassigned"))], [crmLeads]);
  const crmCityOptions = useMemo(() => ["all", ...new Set(crmLeads.map((lead) => lead.city).filter(Boolean))], [crmLeads]);
  const crmSourceOptions = useMemo(() => ["all", ...new Set(crmLeads.map((lead) => lead.lead_source || "resources_page"))], [crmLeads]);
  const crmFilterQuery = new URLSearchParams(
    Object.entries(crmFilters).reduce((acc, [key, value]) => {
      if (value && value !== "all") acc[key] = value;
      return acc;
    }, {})
  ).toString();

  useEffect(() => {
    const boot = async () => {
      try {
        if (sessionToken) {
          if (isCrmPortalRoute && tokenKind === "bearer") {
            try {
              const response = await fetch(`${API}/session/me`, {
                headers: { Authorization: `Bearer ${sessionToken}` },
              });
              if (response.ok) {
                const payload = await response.json();
                if (payload.workspace_type === "crm_admin" || payload.workspace_type === "super_admin") {
                  setCrmPortalAuthenticated(true);
                  setCrmPortalUser(payload.session);
                  setCurrentPage("crm");
                }
              }
            } catch (crmBootError) {
              console.error(crmBootError);
            }
          } else {
            try {
              await loadWorkspaceWithRetry(sessionToken, tokenKind);
            } catch (workspaceError) {
              console.error(workspaceError);
              persistAuth({ token: "", kind: "session" });
              setSessionToken("");
              setTokenKind("session");
              setRefreshToken("");
            }
          }
        }
        await fetchBootstrap();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, []);

  useEffect(() => {
    if (!sessionToken || !searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults(null);
      return undefined;
    }
    const handle = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/search`, {
          params: { q: searchQuery },
          headers: apiHeaders(sessionToken, tokenKind),
        });
        setSearchResults(response.data);
      } catch {
        setSearchResults(null);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [searchQuery, sessionToken, tokenKind]);

  useEffect(() => () => {
    if (documentPreview.url) {
      URL.revokeObjectURL(documentPreview.url);
    }
  }, [documentPreview.url]);

  const workspaceData = workspace?.data;
  const isSuperAdmin = workspace?.workspace_type === "super_admin";
  const isCrmAdmin = workspace?.workspace_type === "crm_admin";
  const isTenant = workspace?.workspace_type === "tenant";
  const tenant = workspaceData?.tenant;

  const navigation = useMemo(() => {
    if (isSuperAdmin) {
      return [
        { key: "platform", label: "Platform Overview", icon: Gauge },
        { key: "tenants", label: "Tenants", icon: Building2 },
        { key: "plans", label: "Plans & Features", icon: Files },
        { key: "library", label: "Public Library", icon: Globe2 },
        { key: "audit", label: "Platform Audit", icon: ShieldCheck },
      ];
    }
    return [
      { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
      { key: "submissions", label: "Submissions", icon: ClipboardList },
      { key: "opinions", label: "Opinions", icon: FileCheck2 },
      { key: "reviews", label: "Reviews", icon: FileSearch },
      { key: "findings", label: "Findings", icon: Radar },
      { key: "meetings", label: "Meetings", icon: Building2 },
      { key: "documents", label: "Documents", icon: FolderOpen },
      { key: "knowledge", label: "Knowledge Hub", icon: Database },
      { key: "reports", label: "Reports", icon: FileBarChart2 },
      { key: "tasks", label: "Tasks & Calendar", icon: Gauge },
      { key: "ai", label: "AI Workspace", icon: BrainCircuit },
      { key: "notifications", label: "Notifications", icon: Activity },
      { key: "audit", label: "Audit Trail", icon: ShieldCheck },
      { key: "admin", label: "Admin", icon: Settings },
    ];
  }, [isSuperAdmin]);

  const onCrmPortalLogin = async () => {
    setLocalLoggingIn(true);
    try {
      const response = await axios.post(`${API}/auth/login`, crmPortalLoginForm);
      const token = response.data.access_token;
      const nextRefreshToken = response.data.refresh_token;
      persistAuth({ token, kind: "bearer", refreshToken: nextRefreshToken });
      setSessionToken(token);
      setTokenKind("bearer");
      setRefreshToken(nextRefreshToken);
      setCrmPortalAuthenticated(true);
      setCrmPortalUser({
        display_name: response.data.user?.name || "CRM Admin",
        role_label: response.data.user?.role_key === "crm_admin" ? "CRM Admin" : response.data.user?.role_key,
      });
      setCurrentPage("crm");
      await fetchCrmOverview();
      toast.success("CRM Admin login berhasil");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal login CRM Admin");
    } finally {
      setLocalLoggingIn(false);
    }
  };

  const onDemoLogin = async () => {
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/auth/demo-login`, loginForm);
      const token = response.data.session.token;
      persistAuth({ token, kind: "session" });
      setSessionToken(token);
      setTokenKind("session");
      setRefreshToken("");
      await loadWorkspaceWithRetry(token, "session");
      fetchBootstrap().catch(() => null);
      toast.success("Session demo aktif");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal login demo");
    } finally {
      setLoggingIn(false);
    }
  };

  const onLocalLogin = async () => {
    setLocalLoggingIn(true);
    try {
      const response = await axios.post(`${API}/auth/login`, localLoginForm);
      const token = response.data.access_token;
      const nextRefreshToken = response.data.refresh_token;
      persistAuth({ token, kind: "bearer", refreshToken: nextRefreshToken });
      setSessionToken(token);
      setTokenKind("bearer");
      setRefreshToken(nextRefreshToken);
      await loadWorkspaceWithRetry(token, "bearer");
      fetchBootstrap().catch(() => null);
      toast.success("Login email/password berhasil");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal login email/password");
    } finally {
      setLocalLoggingIn(false);
    }
  };

  const registerPublicLead = async () => {
    const requiredFields = ["full_name", "email", "institution", "job_title", "whatsapp", "city"];
    const hasEmptyField = requiredFields.some((field) => !resourceForm[field]?.trim());
    if (hasEmptyField) {
      toast.error("Lengkapi semua field registrasi terlebih dahulu");
      return;
    }
    setResourceSubmitting(true);
    try {
      const payload = {
        ...resourceForm,
        interest_manual_key: resourceForm.interest_manual_key === "auto-default" ? "" : resourceForm.interest_manual_key,
        base_url: window.location.origin,
      };
      const response = await axios.post(`${API}/public/resources/register`, payload);
      const nextLead = response.data.lead;
      setResourceLead(nextLead);
      toast.success("Registrasi berhasil. Akses download aman sudah dibuka.");
      window.location.href = nextLead.download_center_url;
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal menyimpan registrasi");
    } finally {
      setResourceSubmitting(false);
    }
  };

  const fetchCrmOverview = async () => {
    if (!isSuperAdmin && !isCrmAdmin && !crmPortalAuthenticated) return;
    setCrmLoading(true);
    try {
      const response = await axios.get(`${API}/super-admin/crm/overview`, { headers: apiHeaders(sessionToken, tokenKind) });
      setCrmOverview(response.data.overview);
      setCrmLeads(response.data.leads || []);
      const nextLeadId = crmSelectedLeadId || response.data.leads?.[0]?.id || "";
      setCrmSelectedLeadId(nextLeadId);
      if (nextLeadId) {
        const leadDetail = response.data.leads.find((lead) => lead.id === nextLeadId) || response.data.leads[0];
        setCrmLeadDetail(leadDetail);
        setCrmLeadUpdateForm({
          pipeline_stage: leadDetail.pipeline_stage || "new",
          owner_name: leadDetail.owner_name || "Superadmin-CRM",
          lead_score: leadDetail.lead_score || 35,
          qualification_note: leadDetail.qualification_note || "",
          next_action_date: leadDetail.next_action_date || "",
          priority: leadDetail.priority || "medium",
          lost_reason: leadDetail.lost_reason || "budget",
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Gagal memuat dashboard CRM");
    } finally {
      setCrmLoading(false);
    }
  };

  const selectCrmLead = (lead) => {
    setCrmSelectedLeadId(lead.id);
    setCrmLeadDetail(lead);
    setCrmLeadUpdateForm({
      pipeline_stage: lead.pipeline_stage || "new",
      owner_name: lead.owner_name || "Superadmin-CRM",
      lead_score: lead.lead_score || 35,
      qualification_note: lead.qualification_note || "",
      next_action_date: lead.next_action_date || "",
      priority: lead.priority || "medium",
      lost_reason: lead.lost_reason || "budget",
    });
  };

  const updateCrmLead = async () => {
    if (!crmSelectedLeadId) return;
    try {
      await axios.patch(`${API}/super-admin/crm/leads/${crmSelectedLeadId}`, crmLeadUpdateForm, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success("Lead CRM diperbarui");
      await fetchCrmOverview();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal memperbarui lead CRM");
    }
  };

  const addCrmNote = async () => {
    if (!crmSelectedLeadId || !crmNoteForm.note.trim()) return;
    try {
      await axios.post(`${API}/super-admin/crm/leads/${crmSelectedLeadId}/notes`, crmNoteForm, { headers: apiHeaders(sessionToken, tokenKind) });

  const exportCrm = (format) => {
    const query = crmFilterQuery ? `?${crmFilterQuery}` : "";
    window.location.href = `${API}/super-admin/crm/export/${format}${query}`;
  };

  const resendCrmEmail = async (eventId) => {
    if (!crmSelectedLeadId) return;
    try {
      await axios.post(`${API}/super-admin/crm/leads/${crmSelectedLeadId}/email-history/${eventId}/resend`, {}, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success("Email berhasil dikirim ulang");
      await fetchCrmOverview();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal mengirim ulang email");
    }
  };

      toast.success("Catatan CRM ditambahkan");
      setCrmNoteForm({ note: "", author: "Superadmin-CRM" });
      await fetchCrmOverview();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal menambah catatan CRM");
    }
  };

  const addCrmFollowup = async () => {
    if (!crmSelectedLeadId || !crmFollowupForm.title.trim() || !crmFollowupForm.due_date) return;
    try {
      await axios.post(`${API}/super-admin/crm/leads/${crmSelectedLeadId}/followups`, crmFollowupForm, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success("Follow-up CRM dijadwalkan");
      setCrmFollowupForm({ title: "", due_date: "", owner_name: crmLeadUpdateForm.owner_name || "Superadmin-CRM", channel: "whatsapp" });
      await fetchCrmOverview();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal menjadwalkan follow-up");
    }
  };

  const addCrmMeeting = async () => {
    if (!crmSelectedLeadId || !crmMeetingForm.scheduled_for) return;
    try {
      await axios.post(`${API}/super-admin/crm/leads/${crmSelectedLeadId}/meetings`, crmMeetingForm, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success("Meeting CRM dijadwalkan");
      setCrmMeetingForm({ title: "Demo DPS360 Cloud", scheduled_for: "", meeting_link: "", notes: "" });
      await fetchCrmOverview();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal menjadwalkan meeting CRM");
    }
  };

  const selectNextManualInterest = async (manualKey) => {
    if (!resourceLead) return;
    try {
      const leadToken = resourceLead.download_center_url.split("/").pop();
      const response = await axios.post(`${API}/public/resources/lead/${leadToken}/select-manual`, {
        manual_key: manualKey,
      });
      setResourceLead(response.data.lead);
      toast.success("Minat manual lanjutan direkam untuk nurturing berikutnya.");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal menyimpan minat manual");
    }
  };

  const downloadResourceManual = (manualKey) => {
    if (!resourceLead) return;
    const leadToken = resourceLead.download_center_url.split("/").pop();
    window.location.href = `${API}/public/resources/lead/${leadToken}/download/${manualKey}`;
  };

  const backToDemoLanding = () => {
    window.location.href = "/";
  };

  const logout = async () => {
    try {
      if (sessionToken && tokenKind === "session") {
        await axios.post(`${API}/auth/logout`, {}, { headers: apiHeaders(sessionToken, tokenKind) });
      }
    } catch {
      // noop
    }
    persistAuth({ token: "", kind: "session" });
    setSessionToken("");
    setTokenKind("session");
    setRefreshToken("");
    setCrmPortalAuthenticated(false);
    setCrmPortalUser(null);
    setWorkspace(null);
    setCurrentPage("");
    setSearchQuery("");
    setSearchResults(null);
    setAiResult(null);
    setDashboardSummary("");
    applyTenantBranding(null);
    toast.success("Session ditutup");
  };

  const refreshWorkspace = async () => {
    try {
      await loadWorkspaceWithRetry(sessionToken, tokenKind);
      await fetchBootstrap();
    } catch (error) {
      console.error(error);
      toast.error("Gagal memuat ulang workspace");
    }
  };

  const createTenant = async () => {
    try {
      await axios.post(`${API}/super-admin/tenants`, tenantForm, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success("Tenant baru berhasil dibuat");
      setTenantForm({
        name: "",
        industry: "Bank Umum Syariah",
        plan_id: "professional",
        primary_color: "175 60% 28%",
        accent_color: "42 65% 52%",
        login_headline: "Workspace DPS baru siap diaktifkan.",
      });
      await refreshWorkspace();
      setCurrentPage("tenants");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal membuat tenant");
    }
  };

  const impersonateTenant = async (tenantId) => {
    try {
      const response = await axios.post(`${API}/super-admin/tenants/${tenantId}/impersonate`, {}, { headers: apiHeaders(sessionToken, tokenKind) });
      const token = response.data.session.token;
      persistAuth({ token, kind: "session" });
      setSessionToken(token);
      setTokenKind("session");
      setRefreshToken("");
      await loadWorkspaceWithRetry(token, "session");
      toast.success("Impersonasi tenant berhasil");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal impersonasi tenant");
    }
  };

  const saveBranding = async () => {
    try {
      await axios.patch(`${API}/tenant/branding`, brandingForm, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success("Branding tenant diperbarui");
      await refreshWorkspace();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal menyimpan branding");
    }
  };

  const createRecord = async (endpoint, payload, resetter) => {
    try {
      await axios.post(`${API}/${endpoint}`, payload, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success("Data berhasil disimpan");
      resetter();
      await refreshWorkspace();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal menyimpan data");
    }
  };

  const uploadDocument = async () => {
    if (!documentUpload.file) {
      toast.error("Pilih file terlebih dahulu");
      return;
    }
    const formData = new FormData();
    formData.append("title", documentUpload.title || documentUpload.file.name);
    formData.append("document_class", documentUpload.document_class);
    formData.append("sensitivity", documentUpload.sensitivity);
    formData.append("unit", documentUpload.unit);
    formData.append("tags", documentUpload.tags);
    formData.append("file", documentUpload.file);
    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: apiHeaders(sessionToken, tokenKind, { "Content-Type": "multipart/form-data" }),
      });
      toast.success("Dokumen berhasil diunggah");
      setDocumentUpload({ title: "", document_class: "internal_policy", sensitivity: "internal", unit: "Sekretariat DPS", tags: "", file: null });
      await refreshWorkspace();
      setCurrentPage("documents");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal mengunggah dokumen");
    }
  };

  const previewDocument = async (doc) => {
    try {
      if (documentPreview.url) {
        URL.revokeObjectURL(documentPreview.url);
      }
      const response = await axios.get(`${API}/documents/${doc.id}/preview`, {
        headers: apiHeaders(sessionToken, tokenKind),
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      setDocumentPreview({ doc, url });
    } catch (error) {
      console.error(error);
      toast.error("Gagal membuka preview dokumen");
    }
  };

  const runAiWorkspace = async (questionOverride) => {
    setAiRunning(true);
    try {
      const payload = {
        ...aiComposer,
        question: questionOverride || aiComposer.question,
      };
      const response = await axios.post(`${API}/ai/workspace/ask`, payload, { headers: apiHeaders(sessionToken, tokenKind) });
      setAiResult(response.data);
      if (questionOverride) {
        setDashboardSummary(response.data.answer);
      }
      toast.success("Draft AI berhasil dibuat");
      await refreshWorkspace();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal menjalankan AI agent");
    } finally {
      setAiRunning(false);
    }
  };

  const saveAISettings = async () => {
    try {
      await axios.patch(
        `${API}/tenant/ai-settings`,
        aiSettingsForm,
        { headers: apiHeaders(sessionToken, tokenKind) }
      );
      toast.success("AI settings tenant diperbarui");
      await refreshWorkspace();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal menyimpan AI settings");
    }
  };

  const opinionWorkflowAction = async (opinionId, action) => {
    try {
      await axios.post(`${API}/opinions/${opinionId}/${action}`, {}, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success(action === "approve" ? "Opini disetujui" : "Opini diajukan ke checker");
      await refreshWorkspace();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Aksi opini gagal");
    }
  };

  const reportWorkflowAction = async (reportId, action) => {
    try {
      await axios.post(`${API}/reports/${reportId}/${action}`, {}, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success(action === "approve" ? "Report disetujui" : action === "submit" ? "Report diajukan ke checker" : "Aksi report berhasil");
      await refreshWorkspace();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Aksi report gagal");
    }
  };

  const generateExecutiveReport = async () => {
    try {
      await axios.post(`${API}/reports/generate-executive-pack`, { title: "Executive Pack Draft AI", report_type: "executive_pack" }, { headers: apiHeaders(sessionToken, tokenKind) });
      toast.success("Executive pack Draft AI berhasil dibuat");
      await refreshWorkspace();
      setCurrentPage("reports");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal membuat executive pack");
    }
  };

  const exportReport = async (reportId, reportTitle) => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}/export`, {
        headers: apiHeaders(sessionToken, tokenKind),
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = `${reportTitle || "report"}.txt`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Export report berhasil diunduh");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Gagal export report");
    }
  };


  if (loading && !isPublicResourcesRoute) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--background))]">
        <div className="surface-card flex w-full max-w-lg items-center gap-4 p-6">
          <div className="h-12 w-12 animate-pulse rounded-2xl bg-[hsl(var(--tenant-primary))]/15" />
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[hsl(var(--tenant-primary))]">DPS360 Cloud</p>
            <h1 className="text-xl font-semibold">Menyiapkan workspace governance...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (isPublicResourcesRoute) {
    return (
      <>
        <PublicResourcesScreen
          catalog={resourceCatalog}
          lead={resourceLead}
          form={resourceForm}
          setForm={setResourceForm}
          onRegister={registerPublicLead}
          registering={resourceSubmitting}
          onDownload={downloadResourceManual}
          onSelectManual={selectNextManualInterest}
          loading={resourceLoading}
          onBackToDemo={backToDemoLanding}
        />
        <Toaster richColors position="top-right" />
      </>
    );
  }

  if (isCrmPortalRoute && !loading && !crmPortalAuthenticated && !isCrmAdmin && !isSuperAdmin) {
    return <CrmPortalLoginScreen form={crmPortalLoginForm} setForm={setCrmPortalLoginForm} onLogin={onCrmPortalLogin} loading={localLoggingIn} />;
  }

  if (!workspace) {
    return (
      <>
        <LoginScreen
          bootstrap={bootstrap}
          form={loginForm}
          setForm={setLoginForm}
          onDemoLogin={onDemoLogin}
          loading={loggingIn}
          localLoginForm={localLoginForm}
          setLocalLoginForm={setLocalLoginForm}
          onLocalLogin={onLocalLogin}
          localLoading={localLoggingIn}
        />
        <ExitIntentPopup open={exitIntentVisible} onClose={() => setExitIntentVisible(false)} />
      </>
    );
  }

  const renderSuperAdminOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Total Tenants" value={numberFormatter.format(workspaceData.kpis.total_tenants)} helper="Tenant demo aktif di platform" tone="info" testId="platform-kpi-total-tenants" />
        <MetricCard title="Active Tenants" value={numberFormatter.format(workspaceData.kpis.active_tenants)} helper="Billing aktif & tidak disuspend" tone="success" testId="platform-kpi-active-tenants" />
        <MetricCard title="Estimated MRR" value={currencyFormatter.format(workspaceData.kpis.estimated_mrr)} helper="Placeholder komersial untuk sales pipeline" tone="warning" testId="platform-kpi-mrr" />
        <MetricCard title="Past Due" value={numberFormatter.format(workspaceData.kpis.overdue_accounts)} helper="Akun butuh follow-up billing" tone="danger" testId="platform-kpi-past-due" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
          <CardHeader>
            <CardTitle>Distribusi Paket</CardTitle>
            <CardDescription>Distribusi tenant berdasarkan subscription plan untuk pipeline enterprise.</CardDescription>
          </CardHeader>
          <CardContent className="h-[320px] pt-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={workspaceData.charts.plan_distribution}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} fill="hsl(var(--tenant-primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
          <CardHeader>
            <CardTitle>Usage Snapshot</CardTitle>
            <CardDescription>AI usage, storage, dan adopsi user aktif per tenant.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            {workspaceData.charts.ai_usage_by_tenant.map((item, index) => (
              <div key={item.tenant} className="rounded-2xl border bg-slate-50/70 p-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="font-medium text-slate-900">{item.tenant}</p>
                  <span className="text-sm text-muted-foreground">AI {item.value} req</span>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <p className="mb-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">Storage</p>
                    <Progress value={Math.min(workspaceData.charts.storage_usage_by_tenant[index]?.value || 0, 100)} className="h-2.5 bg-slate-200" />
                  </div>
                  <div>
                    <p className="mb-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">Users</p>
                    <Progress value={Math.min((workspaceData.charts.active_users_by_tenant[index]?.value || 0) * 4, 100)} className="h-2.5 bg-slate-200" />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderTenantsPage = () => (
    <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
      <TableSection
        title="Daftar Tenant"
        description="Kelola lifecycle tenant, status billing, dan action impersonasi secara aman."
        testId="super-admin-tenant-table"
        rows={workspaceData.tenants}
        columns={[
          { key: "name", label: "Tenant", render: (row) => <div><p className="font-semibold text-slate-900">{row.name}</p><p className="text-xs text-muted-foreground">{row.industry}</p></div> },
          { key: "plan_id", label: "Plan", render: (row) => <StatusBadge value={row.plan_id} /> },
          { key: "billing_status", label: "Billing", render: (row) => <StatusBadge value={row.billing_status} /> },
          { key: "renewal_date", label: "Renewal", render: (row) => formatDate(row.renewal_date) },
          {
            key: "actions",
            label: "Actions",
            render: (row) => (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => impersonateTenant(row.id)} data-testid={`impersonate-${row.id}`}>
                  Impersonate
                </Button>
              </div>
            ),
          },
        ]}
        emptyMessage="Tenant baru yang dibuat akan muncul di sini beserta status billing dan plan aktifnya."
      />
      <ComposerCard
        title="Provision Tenant Baru"
        description="Buat workspace baru, tetapkan plan awal, lalu siapkan branding dan admin tenant seed."
        testId="super-admin-create-tenant"
        submitLabel="Buat Tenant"
        formState={tenantForm}
        setFormState={setTenantForm}
        onSubmit={createTenant}
        fields={[
          { name: "name", label: "Nama Tenant", placeholder: "Contoh: Fintech Amanah Digital", testId: "tenant-create-name" },
          { name: "industry", label: "Jenis Institusi", placeholder: "Fintech Syariah", testId: "tenant-create-industry" },
          {
            name: "plan_id",
            label: "Paket",
            type: "select",
            testId: "tenant-create-plan",
            options: (bootstrap?.plans || []).map((plan) => ({ value: plan.id, label: plan.name })),
          },
          { name: "primary_color", label: "Primary Color (HSL)", placeholder: "175 60% 28%", testId: "tenant-create-primary" },
          { name: "accent_color", label: "Accent Color (HSL)", placeholder: "42 65% 52%", testId: "tenant-create-accent" },
          { name: "login_headline", label: "Login Headline", placeholder: "Copy enterprise untuk login screen", type: "textarea", span: 2, testId: "tenant-create-headline" },
        ]}
      />
    </div>
  );

  const renderPlansPage = () => (
    <div className="space-y-6">
      <div className="grid gap-4 xl:grid-cols-4">
        {workspaceData.plans.map((plan) => (
          <Card key={plan.id} className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>{plan.name}</CardTitle>
                <StatusBadge value={plan.id} />
              </div>
              <CardDescription>{plan.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              <p className="text-2xl font-semibold text-slate-950">{currencyFormatter.format(plan.price_monthly)}<span className="text-sm font-normal text-muted-foreground">/bulan</span></p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Maks pengguna: {plan.limits.max_users}</p>
                <p>Maks cabang: {plan.limits.max_branches}</p>
                <p>Kuota AI: {plan.limits.max_ai_requests}/bulan</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <TableSection
        title="Feature Matrix"
        description="Entitlement utama yang membedakan paket komersial DPS360 Cloud."
        testId="super-admin-plan-matrix"
        rows={workspaceData.plans.map((plan) => ({
          id: plan.id,
          name: plan.name,
          ai_workspace: plan.features.ai_workspace ? "Ya" : "Tidak",
          white_label: plan.features.white_label ? "Ya" : "Tidak",
          advanced_audit: plan.features.advanced_audit ? "Ya" : "Tidak",
          custom_workflows: plan.features.custom_workflows ? "Ya" : "Tidak",
          api_access: plan.features.api_access ? "Ya" : "Tidak",
        }))}
        columns={[
          { key: "name", label: "Plan" },
          { key: "ai_workspace", label: "AI Workspace" },
          { key: "white_label", label: "White-label" },
          { key: "advanced_audit", label: "Advanced Audit" },
          { key: "custom_workflows", label: "Custom Workflow" },
          { key: "api_access", label: "API Access" },
        ]}
        emptyMessage="Belum ada data plan."
      />
    </div>
  );

  const renderPublicLibraryPage = () => (
    <TableSection
      title="Public Knowledge Library"
      description="Layer referensi publik yang dapat dipakai oleh tenant sesuai konteks dan kebijakan grounding."
      testId="super-admin-public-library"
      rows={workspaceData.public_library}
      columns={[
        { key: "title", label: "Judul" },
        { key: "category", label: "Kategori", render: (row) => <StatusBadge value={row.category} /> },
        { key: "content", label: "Ringkasan", render: (row) => <p className="max-w-[520px] text-sm text-muted-foreground">{row.content}</p> },
        { key: "tags", label: "Tags", render: (row) => <div className="flex flex-wrap gap-2">{(row.tags || []).map((tag) => <Badge key={tag} variant="outline" className="rounded-full">{tag}</Badge>)}</div> },
      ]}
      emptyMessage="Referensi publik akan muncul di sini."
    />
  );

  const renderPlatformAuditPage = () => (
    <TableSection
      title="Platform Audit"
      description="Jejak audit lintas tenant untuk login, provisioning, impersonasi, dan aktivitas kritikal lain."
      testId="super-admin-audit-table"
      rows={workspaceData.platform_audit}
      columns={[
        { key: "created_at", label: "Waktu", render: (row) => formatDate(row.created_at) },
        { key: "actor_name", label: "Actor" },
        { key: "module", label: "Module", render: (row) => <StatusBadge value={row.module} /> },
        { key: "action", label: "Action", render: (row) => <StatusBadge value={row.action} /> },
        { key: "detail", label: "Detail" },
      ]}
      emptyMessage="Belum ada audit log platform."
    />
  );


  const renderSuperAdminCrm = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard title="Total Leads" value={numberFormatter.format(crmOverview?.kpis?.total_leads || 0)} helper="Leads masuk dari landing page resources" tone="info" testId="crm-kpi-total-leads" />
        <MetricCard title="Qualified" value={numberFormatter.format(crmOverview?.kpis?.qualified_leads || 0)} helper="Lead siap masuk tahap demo/proposal" tone="success" testId="crm-kpi-qualified" />
        <MetricCard title="Demo Scheduled" value={numberFormatter.format(crmOverview?.kpis?.demo_scheduled || 0)} helper="Meeting demo yang sudah terjadwal" tone="warning" testId="crm-kpi-demo" />
        <MetricCard title="Won Deals" value={numberFormatter.format(crmOverview?.kpis?.won_deals || 0)} helper="Konversi menjadi peluang menang" tone="success" testId="crm-kpi-won" />
        <MetricCard title="Conversion Rate" value={`${crmOverview?.kpis?.conversion_rate || 0}%`} helper="Won dibanding total leads" tone="info" testId="crm-kpi-conversion" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
          <CardHeader>
            <CardTitle>Pipeline CRM</CardTitle>
            <CardDescription>Monitor kinerja produk demo dps-360.com melalui tahapan pipeline penjualan.</CardDescription>
          </CardHeader>
          <CardContent className="h-[320px] pt-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={crmOverview?.pipeline || []}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[8, 8, 0, 0]} fill="hsl(var(--tenant-primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
          <CardHeader>
            <CardTitle>Lead Source, City & Interest Snapshot</CardTitle>
            <CardDescription>Distribusi source, kota, dan minat manual untuk membaca demand demo product.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border bg-slate-50/70 p-4">
                <p className="mb-3 text-sm font-semibold text-slate-900">Sources</p>
                <div className="space-y-2 text-sm text-muted-foreground">
                  {(crmOverview?.sources || []).map((item) => (
                    <div key={item.name} className="flex items-center justify-between gap-3"><span>{item.name}</span><span className="font-medium text-slate-900">{item.value}</span></div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border bg-slate-50/70 p-4">
                <p className="mb-3 text-sm font-semibold text-slate-900">Top Cities</p>
                <div className="space-y-2 text-sm text-muted-foreground">
                  {(crmOverview?.cities || []).map((item) => (
                    <div key={item.name} className="flex items-center justify-between gap-3"><span>{item.name}</span><span className="font-medium text-slate-900">{item.value}</span></div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl border bg-white p-4">
              <p className="mb-3 text-sm font-semibold text-slate-900">Interest Heatmap</p>
              <div className="flex flex-wrap gap-2">
                {(crmOverview?.interests || []).map((item) => (
                  <Badge key={item.name} variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-3 py-1.5 text-slate-700">{item.name} · {item.value}</Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
          <CardHeader>
            <CardTitle>Lost-Reason Analytics</CardTitle>
            <CardDescription>Alasan loss default untuk membaca hambatan penjualan dan kebutuhan perbaikan positioning.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {(crmOverview?.lost_reasons || []).length ? (crmOverview.lost_reasons.map((item) => (
              <div key={item.name} className="flex items-center justify-between rounded-2xl border bg-slate-50/70 px-4 py-3 text-sm">
                <span className="font-medium text-slate-900">{item.name}</span>
                <Badge variant="outline" className="rounded-full">{item.value}</Badge>
              </div>
            ))) : <EmptyState title="Belum ada data lost reason" description="Data analitik loss akan muncul saat pipeline lead mulai memiliki outcome lost." />}
          </CardContent>
        </Card>
        <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
          <CardHeader>
            <CardTitle>Owner Performance</CardTitle>
            <CardDescription>Kinerja owner berdasarkan jumlah lead, demos, won deals, score rata-rata, dan follow-up overdue.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {(crmOverview?.owner_performance || []).map((owner) => (
              <div key={owner.owner_name} className="rounded-2xl border bg-slate-50/70 p-4">
                <div className="mb-3 flex items-center justify-between gap-3"><p className="font-semibold text-slate-900">{owner.owner_name}</p><Badge variant="outline" className="rounded-full">Won {owner.won_deals}</Badge></div>
                <div className="grid gap-2 text-sm text-muted-foreground md:grid-cols-3">
                  <p>Leads: <span className="font-medium text-slate-900">{owner.leads_handled}</span></p>
                  <p>Demos: <span className="font-medium text-slate-900">{owner.demos_scheduled}</span></p>
                  <p>Avg Score: <span className="font-medium text-slate-900">{owner.average_lead_score}</span></p>
                  <p>Open Follow-up: <span className="font-medium text-slate-900">{owner.followups_open}</span></p>
                  <p>Overdue: <span className="font-medium text-slate-900">{owner.followups_overdue}</span></p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>


      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <div className="space-y-6">
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <CardTitle>CRM Filters & Export</CardTitle>
              <CardDescription>Filter leads berdasarkan pipeline, owner, kota, prioritas, source, lalu ekspor CSV/Excel sesuai kebutuhan sales ops.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3 xl:grid-cols-6 pt-0">
              <FormField field={{ name: "pipeline_stage", label: "Pipeline", type: "select", testId: "crm-filter-pipeline", options: crmPipelineStages }} value={crmFilters.pipeline_stage} onChange={(name, value) => setCrmFilters((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "owner_name", label: "Owner", type: "select", testId: "crm-filter-owner", options: crmOwnerOptions.map((item) => ({ value: item, label: item === 'all' ? 'Semua Owner' : item })) }} value={crmFilters.owner_name} onChange={(name, value) => setCrmFilters((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "city", label: "Kota", type: "select", testId: "crm-filter-city", options: crmCityOptions.map((item) => ({ value: item, label: item === 'all' ? 'Semua Kota' : item })) }} value={crmFilters.city} onChange={(name, value) => setCrmFilters((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "priority", label: "Priority", type: "select", testId: "crm-filter-priority", options: [
                { value: 'all', label: 'Semua Priority' },
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
              ] }} value={crmFilters.priority} onChange={(name, value) => setCrmFilters((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "source", label: "Source", type: "select", testId: "crm-filter-source", options: crmSourceOptions.map((item) => ({ value: item, label: item === 'all' ? 'Semua Source' : item })) }} value={crmFilters.source} onChange={(name, value) => setCrmFilters((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "search", label: "Search", testId: "crm-filter-search", placeholder: "Cari nama/email/institusi" }} value={crmFilters.search} onChange={(name, value) => setCrmFilters((current) => ({ ...current, [name]: value }))} />
            </CardContent>
          </Card>

          {crmViewMode === "table" ? (
            <TableSection
              title="Lead Pipeline Register"
              description="Daftar seluruh potential customers yang masuk dari public resources page dan popup lead capture."
              testId="crm-leads-table"
              rows={filteredCrmLeads}
              columns={[
                { key: "full_name", label: "Lead", render: (row) => <button type="button" className="text-left" onClick={() => selectCrmLead(row)}><p className="font-medium text-slate-900">{row.full_name}</p><p className="text-xs text-muted-foreground">{row.institution}</p></button> },
                { key: "job_title", label: "Jabatan" },
                { key: "city", label: "Kota" },
                { key: "pipeline_stage", label: "Pipeline", render: (row) => <StatusBadge value={row.pipeline_stage} /> },
                { key: "owner_name", label: "Owner" },
                { key: "next_action_date", label: "Next Action", render: (row) => formatDate(row.next_action_date) },
              ]}
              emptyMessage="Belum ada lead yang masuk ke CRM."
              toolbar={
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" className="rounded-xl" onClick={fetchCrmOverview}>Refresh CRM</Button>
                  <Button variant={crmViewMode === "table" ? "default" : "outline"} className="rounded-xl" onClick={() => setCrmViewMode("table")}>Table</Button>
                  <Button variant={crmViewMode === "kanban" ? "default" : "outline"} className="rounded-xl" onClick={() => setCrmViewMode("kanban")}>Kanban</Button>
                  <Button variant="outline" className="rounded-xl" onClick={() => exportCrm("csv")}>Export CSV</Button>
                  <Button variant="outline" className="rounded-xl" onClick={() => exportCrm("xlsx")}>Export Excel</Button>
                </div>
              }
            />
          ) : (
            <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
              <CardHeader>
                <CardTitle>Kanban Pipeline</CardTitle>
                <CardDescription>Lihat distribusi lead per stage dan klik card untuk membuka detail CRM.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 overflow-x-auto xl:grid-cols-7 pt-0">
                {crmPipelineStages.filter((item) => item.value !== 'all').map((stage) => (
                  <div key={stage.value} className="min-w-[220px] rounded-2xl border bg-slate-50/70 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3"><p className="font-semibold text-slate-900">{stage.label}</p><Badge variant="outline" className="rounded-full">{filteredCrmLeads.filter((lead) => lead.pipeline_stage === stage.value).length}</Badge></div>
                    <div className="space-y-3">
                      {filteredCrmLeads.filter((lead) => lead.pipeline_stage === stage.value).map((lead) => (
                        <button key={lead.id} type="button" onClick={() => selectCrmLead(lead)} className="w-full rounded-2xl border bg-white p-4 text-left hover:bg-slate-50">
                          <p className="font-medium text-slate-900">{lead.full_name}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{lead.institution}</p>
                          <p className="mt-2 text-xs text-muted-foreground">Owner: {lead.owner_name || 'Unassigned'}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <CardTitle>{crmLeadDetail ? `Lead Detail — ${crmLeadDetail.full_name}` : "Lead Detail"}</CardTitle>
              <CardDescription>{crmLeadDetail ? `${crmLeadDetail.institution} · ${crmLeadDetail.job_title}` : "Pilih lead dari tabel untuk melihat detail dan aktivitas penjualan."}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              {crmLeadDetail ? (
                <>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl border bg-slate-50/70 p-4"><p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Email</p><p className="mt-2 text-sm font-medium text-slate-900">{crmLeadDetail.email}</p></div>
                    <div className="rounded-2xl border bg-slate-50/70 p-4"><p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">WhatsApp</p><p className="mt-2 text-sm font-medium text-slate-900">{crmLeadDetail.whatsapp}</p></div>
                  </div>
                  <div className="grid gap-4">
                    <FormField field={{ name: "pipeline_stage", label: "Pipeline Stage", type: "select", testId: "crm-pipeline-stage-select", options: [
                      { value: "new", label: "New" },
                      { value: "contacted", label: "Contacted" },
                      { value: "qualified", label: "Qualified" },
                      { value: "demo_scheduled", label: "Demo Scheduled" },
                      { value: "proposal", label: "Proposal" },
                      { value: "won", label: "Won" },
                      { value: "lost", label: "Lost" },
                    ] }} value={crmLeadUpdateForm.pipeline_stage} onChange={(name, value) => setCrmLeadUpdateForm((current) => ({ ...current, [name]: value }))} />
                    <FormField field={{ name: "owner_name", label: "Owner", testId: "crm-owner-name-input", placeholder: "Superadmin-CRM" }} value={crmLeadUpdateForm.owner_name} onChange={(name, value) => setCrmLeadUpdateForm((current) => ({ ...current, [name]: value }))} />
                    <FormField field={{ name: "lead_score", label: "Lead Score", type: "number", testId: "crm-lead-score-input", placeholder: "35" }} value={crmLeadUpdateForm.lead_score} onChange={(name, value) => setCrmLeadUpdateForm((current) => ({ ...current, [name]: Number(value) }))} />
                    <FormField field={{ name: "next_action_date", label: "Next Action Date", type: "date", testId: "crm-next-action-date-input" }} value={crmLeadUpdateForm.next_action_date} onChange={(name, value) => setCrmLeadUpdateForm((current) => ({ ...current, [name]: value }))} />
                    <FormField field={{ name: "priority", label: "Priority", type: "select", testId: "crm-priority-select", options: [
                      { value: "low", label: "Low" },
                      { value: "medium", label: "Medium" },
                      { value: "high", label: "High" },
                    ] }} value={crmLeadUpdateForm.priority} onChange={(name, value) => setCrmLeadUpdateForm((current) => ({ ...current, [name]: value }))} />
                    {crmLeadUpdateForm.pipeline_stage === "lost" ? <FormField field={{ name: "lost_reason", label: "Lost Reason", type: "select", testId: "crm-lost-reason-select", options: crmLostReasons }} value={crmLeadUpdateForm.lost_reason || crmLostReasons[0].value} onChange={(name, value) => setCrmLeadUpdateForm((current) => ({ ...current, [name]: value }))} /> : null}
                    <FormField field={{ name: "qualification_note", label: "Qualification Note", type: "textarea", testId: "crm-qualification-note-input", placeholder: "Catatan kualifikasi, kebutuhan, atau readiness lead" }} value={crmLeadUpdateForm.qualification_note} onChange={(name, value) => setCrmLeadUpdateForm((current) => ({ ...current, [name]: value }))} />
                    <Button className="rounded-xl" onClick={updateCrmLead} data-testid="crm-update-lead-button">Simpan Update CRM</Button>
                  </div>
                </>
              ) : <EmptyState title="Belum ada lead terpilih" description="Klik salah satu lead di tabel untuk membuka workspace Superadmin-CRM." />}
            </CardContent>
          </Card>
        </div>
      </div>

      {crmLeadDetail ? (
        <div className="grid gap-6 xl:grid-cols-3">
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader><CardTitle>Notes & Qualification</CardTitle><CardDescription>Catatan penjualan, insight kebutuhan, dan konteks follow-up lead.</CardDescription></CardHeader>
            <CardContent className="space-y-4 pt-0">
              <Textarea value={crmNoteForm.note} onChange={(event) => setCrmNoteForm((current) => ({ ...current, note: event.target.value }))} placeholder="Tambahkan catatan CRM" data-testid="crm-note-input" className="min-h-[120px] bg-white" />
              <Button className="w-full rounded-xl" onClick={addCrmNote} data-testid="crm-add-note-button">Tambah Catatan</Button>
              <div className="space-y-3">
                {(crmLeadDetail.notes || []).map((note) => (
                  <div key={note.id} className="rounded-2xl border bg-slate-50/70 p-4"><p className="text-sm font-medium text-slate-900">{note.author}</p><p className="mt-1 text-xs text-muted-foreground">{formatDate(note.created_at)}</p><p className="mt-3 text-sm text-muted-foreground">{note.note}</p></div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader><CardTitle>Follow-up & Tasks</CardTitle><CardDescription>Atur owner, kanal follow-up, dan tenggat aktivitas sales berikutnya.</CardDescription></CardHeader>
            <CardContent className="space-y-4 pt-0">
              <FormField field={{ name: "title", label: "Judul Follow-up", testId: "crm-followup-title-input", placeholder: "Kirim follow-up WhatsApp" }} value={crmFollowupForm.title} onChange={(name, value) => setCrmFollowupForm((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "due_date", label: "Due Date", type: "date", testId: "crm-followup-date-input" }} value={crmFollowupForm.due_date} onChange={(name, value) => setCrmFollowupForm((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "owner_name", label: "Owner", testId: "crm-followup-owner-input", placeholder: "Superadmin-CRM" }} value={crmFollowupForm.owner_name} onChange={(name, value) => setCrmFollowupForm((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "channel", label: "Channel", type: "select", testId: "crm-followup-channel-select", options: [
                { value: "whatsapp", label: "WhatsApp" },
                { value: "email", label: "Email" },
                { value: "call", label: "Call" },
              ] }} value={crmFollowupForm.channel} onChange={(name, value) => setCrmFollowupForm((current) => ({ ...current, [name]: value }))} />
              <Button className="w-full rounded-xl" onClick={addCrmFollowup} data-testid="crm-add-followup-button">Tambah Follow-up</Button>
              <div className="space-y-3">
                {(crmLeadDetail.followups || []).map((item) => (
                  <div key={item.id} className="rounded-2xl border bg-slate-50/70 p-4"><div className="flex items-center justify-between gap-3"><p className="font-medium text-slate-900">{item.title}</p><StatusBadge value={item.status} /></div><p className="mt-1 text-sm text-muted-foreground">{item.owner_name} · {item.channel} · {formatDate(item.due_date)}</p></div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader><CardTitle>Meetings & Email Activity</CardTitle><CardDescription>Jadwalkan demo/meeting dan monitor histori email nurturing, resend, serta status kegagalan kirim.</CardDescription></CardHeader>
            <CardContent className="space-y-4 pt-0">
              <FormField field={{ name: "title", label: "Judul Meeting", testId: "crm-meeting-title-input", placeholder: "Demo DPS360 Cloud" }} value={crmMeetingForm.title} onChange={(name, value) => setCrmMeetingForm((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "scheduled_for", label: "Jadwal Meeting", type: "date", testId: "crm-meeting-date-input" }} value={crmMeetingForm.scheduled_for} onChange={(name, value) => setCrmMeetingForm((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "meeting_link", label: "Meeting Link", testId: "crm-meeting-link-input", placeholder: "https://meet.google.com/..." }} value={crmMeetingForm.meeting_link} onChange={(name, value) => setCrmMeetingForm((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "notes", label: "Catatan Meeting", type: "textarea", testId: "crm-meeting-notes-input", placeholder: "Agenda meeting atau target demo" }} value={crmMeetingForm.notes} onChange={(name, value) => setCrmMeetingForm((current) => ({ ...current, [name]: value }))} />
              <Button className="w-full rounded-xl" onClick={addCrmMeeting} data-testid="crm-add-meeting-button">Jadwalkan Meeting</Button>
              <div className="space-y-3">
                {(crmLeadDetail.meetings || []).map((meeting) => (
                  <div key={meeting.id} className="rounded-2xl border bg-slate-50/70 p-4"><p className="font-medium text-slate-900">{meeting.title}</p><p className="mt-1 text-sm text-muted-foreground">{formatDate(meeting.scheduled_for)} · {meeting.status}</p></div>
                ))}
              </div>
              <Separator className="my-2" />
              <div className="space-y-3">
                {(crmLeadDetail.nurture_email_history || []).map((item) => (
                  <div key={`${item.id || item.day}-${item.subject}`} className="rounded-2xl border bg-white p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">Hari {item.day}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{item.subject}</p>
                      </div>
                      <Badge variant="outline" className="rounded-full">{item.status}</Badge>
                    </div>
                    <div className="mt-3 grid gap-2 text-sm text-muted-foreground">
                      <p>Due: {formatDate(item.due_at)}</p>
                      {item.sent_at ? <p>Sent: {formatDate(item.sent_at)}</p> : null}
                      {item.last_error ? <p className="text-rose-700">Error: {item.last_error}</p> : null}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="outline" className="rounded-xl" onClick={() => resendCrmEmail(item.id)} data-testid={`crm-resend-email-${item.day}`}>Resend Email</Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );

  const renderTenantDashboard = () => {
    const dashboard = workspaceData.dashboard;
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard title="Temuan Terbuka" value={dashboard.kpis.open_findings} helper="War room governance aktif" tone="danger" testId="executive-kpi-open-findings" />
          <MetricCard title="Temuan Overdue" value={dashboard.kpis.overdue_findings} helper="Perlu eskalasi ke owner" tone="warning" testId="executive-kpi-overdue-findings" />
          <MetricCard title="Opini Pending" value={dashboard.kpis.pending_opinions} helper="Dalam review DPS" tone="info" testId="executive-kpi-pending-opinions" />
          <MetricCard title="Submission Aktif" value={dashboard.kpis.pending_submissions} helper="Masih di workflow" tone="info" testId="executive-kpi-submissions" />
          <MetricCard title="Action Completed" value={`${dashboard.kpis.action_completion_rate}%`} helper="Kinerja penyelesaian tindakan" tone="success" testId="executive-kpi-action-rate" />
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <CardTitle>Trend Temuan per Bulan</CardTitle>
              <CardDescription>Ringkasan issue pattern untuk eksekutif dan sekretariat DPS.</CardDescription>
            </CardHeader>
            <CardContent className="h-[300px] pt-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dashboard.trend}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="temuan" stroke="hsl(var(--tenant-primary))" fill="hsl(var(--tenant-primary))" fillOpacity={0.18} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle>Ringkasan Draft AI</CardTitle>
                  <CardDescription>Gunakan dokumen tenant terbaru untuk merangkum risiko dan tindakan prioritas.</CardDescription>
                </div>
                <Badge className="rounded-full bg-[hsl(var(--tenant-primary))] text-white">Draft AI</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              <div className="rounded-2xl border bg-slate-50/80 p-4 text-sm leading-7 text-slate-700">
                {dashboardSummary || "Belum ada ringkasan. Klik tombol di bawah untuk menghasilkan executive summary dengan citation dari dokumen tenant aktif."}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  className="rounded-xl"
                  onClick={() => runAiWorkspace("Buat ringkasan eksekutif DPS untuk tenant ini. Jelaskan temuan material, opini pending, dan tindakan prioritas minggu ini.")}
                  disabled={aiRunning || !workspaceData.ai_settings.enabled}
                  data-testid="dashboard-generate-ai-summary"
                >
                  {aiRunning ? "Memproses..." : "Buat Ringkasan Draft AI"}
                </Button>
                {!workspaceData.ai_settings.enabled ? <Badge variant="outline">Tersedia di paket Professional ke atas</Badge> : null}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <CardTitle>Severity Matrix</CardTitle>
              <CardDescription>Distribusi severity temuan aktif.</CardDescription>
            </CardHeader>
            <CardContent className="h-[280px] pt-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={dashboard.severity_breakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={4}>
                    {dashboard.severity_breakdown.map((entry, index) => (
                      <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <CardTitle>Branch Heatmap & Watchlist</CardTitle>
              <CardDescription>Unit/cabang dengan intensitas issue paling tinggi dan pengajuan yang perlu perhatian.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-5 pt-0 md:grid-cols-2">
              <div className="space-y-3">
                {dashboard.heatmap.length ? dashboard.heatmap.map((item) => (
                  <div key={item.branch} className="rounded-2xl border bg-slate-50/70 p-4">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-900">{item.branch}</p>
                      <StatusBadge value={item.severity} />
                    </div>
                    <Progress value={Math.min(item.score * 25, 100)} className="h-2.5 bg-slate-200" />
                  </div>
                )) : <p className="text-sm text-muted-foreground">Belum ada data heatmap.</p>}
              </div>
              <div className="space-y-3">
                {dashboard.watchlist.map((item) => (
                  <div key={item.product} className="rounded-2xl border bg-white p-4">
                    <p className="font-medium text-slate-900">{item.product}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{item.branch}</p>
                    <div className="mt-3"><StatusBadge value={item.status} /></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderReviews = () => (
    <TableSection
      title="Review Management"
      description="Tracking review coverage, reviewer aktif, dan skor checklist untuk submission yang sedang dianalisis."
      testId="reviews-table"
      rows={workspaceData.reviews}
      columns={[
        { key: "title", label: "Review" },
        { key: "reviewer", label: "Reviewer" },
        { key: "coverage", label: "Coverage", render: (row) => `${row.coverage}%` },
        { key: "checklist_score", label: "Checklist", render: (row) => `${row.checklist_score}%` },
        { key: "status", label: "Status", render: (row) => <StatusBadge value={row.status} /> },
      ]}
      emptyMessage="Review checklist dan coverage akan muncul di sini."
    />
  );

  const renderDocuments = () => (
    <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      <div className="space-y-6">
        <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
          <CardHeader>
            <CardTitle>Upload Evidence & Preview</CardTitle>
            <CardDescription>Unggah dokumen nyata tenant, tag metadata, lalu buka preview inline dari evidence vault.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <div className="form-grid form-grid--two">
              <FormField field={{ name: "title", label: "Judul Dokumen", testId: "documents-title-input", placeholder: "Contoh: Memo DPS Produk Baru" }} value={documentUpload.title} onChange={(name, value) => setDocumentUpload((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "document_class", label: "Kelas Dokumen", type: "select", testId: "documents-class-select", options: [
                { value: "internal_policy", label: "Internal Policy" },
                { value: "minutes", label: "Minutes" },
                { value: "review_memo", label: "Review Memo" },
                { value: "correspondence", label: "Correspondence" },
              ] }} value={documentUpload.document_class} onChange={(name, value) => setDocumentUpload((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "sensitivity", label: "Sensitivitas", type: "select", testId: "documents-sensitivity-select", options: [
                { value: "internal", label: "Internal" },
                { value: "confidential", label: "Confidential" },
                { value: "restricted", label: "Restricted" },
              ] }} value={documentUpload.sensitivity} onChange={(name, value) => setDocumentUpload((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "unit", label: "Unit", testId: "documents-unit-input", placeholder: "Sekretariat DPS" }} value={documentUpload.unit} onChange={(name, value) => setDocumentUpload((current) => ({ ...current, [name]: value }))} />
              <FormField field={{ name: "tags", label: "Tags", testId: "documents-tags-input", placeholder: "mis: murabahah, board-pack", span: 2 }} value={documentUpload.tags} onChange={(name, value) => setDocumentUpload((current) => ({ ...current, [name]: value }))} />
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">File</label>
                <Input data-testid="documents-file-input" type="file" onChange={(event) => setDocumentUpload((current) => ({ ...current, file: event.target.files?.[0] || null }))} className="bg-white" />
              </div>
            </div>
            <Button onClick={uploadDocument} className="w-full rounded-xl" data-testid="documents-upload-primary-button">
              Upload Dokumen
            </Button>
          </CardContent>
        </Card>

        <TableSection
          title="Documents Library"
          description="Library tenant-aware dengan preview, tags, dan linked metadata."
          testId="documents-library-table"
          rows={workspaceData.documents}
          columns={[
            { key: "title", label: "Dokumen", render: (row) => <div><p className="font-medium text-slate-900">{row.title}</p><p className="text-xs text-muted-foreground">{row.original_filename}</p></div> },
            { key: "document_class", label: "Kelas", render: (row) => <StatusBadge value={row.document_class} /> },
            { key: "sensitivity", label: "Sensitivitas", render: (row) => <StatusBadge value={row.sensitivity} /> },
            { key: "unit", label: "Unit" },
            { key: "actions", label: "Preview", render: (row) => <Button size="sm" variant="outline" onClick={() => previewDocument(row)} data-testid={`documents-preview-${row.id}`}>Buka</Button> },
          ]}
          emptyMessage="Belum ada dokumen tenant. Gunakan form upload untuk menambah evidence baru."
        />
      </div>

      <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
        <CardHeader>
          <CardTitle>Preview & Metadata</CardTitle>
          <CardDescription>Buka dokumen dari library untuk melihat preview inline dan metadata utamanya.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          {documentPreview.doc ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border bg-slate-50/60 p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Judul</p>
                  <p className="mt-2 font-semibold text-slate-900">{documentPreview.doc.title}</p>
                </div>
                <div className="rounded-2xl border bg-slate-50/60 p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Metadata</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <StatusBadge value={documentPreview.doc.document_class} />
                    <StatusBadge value={documentPreview.doc.sensitivity} />
                  </div>
                </div>
              </div>
              {documentPreview.doc.content_type?.includes("pdf") ? (
                <iframe title={documentPreview.doc.title} src={documentPreview.url} className="preview-frame" />
              ) : (
                <div className="preview-frame p-6">
                  <pre className="whitespace-pre-wrap text-sm leading-7 text-slate-700">{documentPreview.doc.extracted_preview || "Preview teks tidak tersedia."}</pre>
                </div>
              )}
            </>
          ) : (
            <EmptyState title="Preview belum dipilih" description="Klik tombol Buka pada salah satu dokumen untuk melihat preview inline di panel ini." />
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderAiWorkspace = () => (
    <div className="grid gap-6 xl:grid-cols-[280px_1fr_360px]">
      <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
        <CardHeader>
          <CardTitle>AI Agents</CardTitle>
          <CardDescription>Pilih agent premium sesuai use case governance Anda.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {workspaceData.ai_settings.agents.map((agent) => (
            <button
              key={agent}
              type="button"
              onClick={() => setAiComposer((current) => ({ ...current, agent_name: agent }))}
              className={cn(
                "w-full rounded-2xl border px-4 py-3 text-left transition-colors",
                aiComposer.agent_name === agent ? "border-[hsl(var(--tenant-primary))] bg-[hsl(var(--tenant-primary))]/5" : "bg-slate-50/70 hover:bg-slate-100"
              )}
            >
              <p className="font-medium text-slate-900">{agent}</p>
              <p className="mt-1 text-xs text-muted-foreground">Grounded output dengan citation tenant-aware.</p>
            </button>
          ))}
          <Separator className="my-4" />
          <div className="rounded-2xl border bg-slate-50/70 p-4 text-sm text-muted-foreground">
            <p className="font-medium text-slate-900">Saved prompts</p>
            <div className="mt-3 space-y-2">
              {workspaceData.saved_prompts.map((prompt) => (
                <button key={prompt.id} type="button" onClick={() => setAiComposer((current) => ({ ...current, question: prompt.question }))} className="w-full rounded-xl border bg-white px-3 py-2 text-left text-sm hover:bg-slate-50">
                  {prompt.title}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>AI Workspace</CardTitle>
              <CardDescription>Prompt workbench tenant-aware untuk research, drafting, dan review memo.</CardDescription>
            </div>
            <Badge className="rounded-full bg-[hsl(var(--tenant-primary))] text-white">Draft AI</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Agent aktif</label>
            <Select value={aiComposer.agent_name} onValueChange={(value) => setAiComposer((current) => ({ ...current, agent_name: value }))}>
              <SelectTrigger data-testid="ai-agent-select" className="bg-white">
                <SelectValue placeholder="Pilih agent" />
              </SelectTrigger>
              <SelectContent>
                {workspaceData.ai_settings.agents.map((agent) => (
                  <SelectItem key={agent} value={agent}>{agent}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Grounding sources</label>
            <div className="grid gap-2 md:grid-cols-2">
              {workspaceData.documents.map((doc) => {
                const active = aiComposer.doc_ids.includes(doc.id);
                return (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => setAiComposer((current) => ({
                      ...current,
                      doc_ids: active ? current.doc_ids.filter((item) => item !== doc.id) : [...current.doc_ids, doc.id],
                    }))}
                    className={cn(
                      "rounded-2xl border px-3 py-3 text-left transition-colors",
                      active ? "border-[hsl(var(--tenant-primary))] bg-[hsl(var(--tenant-primary))]/5" : "bg-slate-50/70 hover:bg-slate-100"
                    )}
                  >
                    <p className="text-sm font-medium text-slate-900">{doc.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{doc.document_class}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Prompt</label>
            <Textarea
              data-testid="ai-workspace-question-input"
              value={aiComposer.question}
              onChange={(event) => setAiComposer((current) => ({ ...current, question: event.target.value }))}
              placeholder="Tanyakan sesuatu terkait dokumen, submission, opini, atau temuan tenant ini."
              className="min-h-[180px] bg-white"
            />
          </div>

          <Button
            onClick={() => runAiWorkspace()}
            disabled={!workspaceData.ai_settings.enabled || aiRunning || !aiComposer.question.trim()}
            className="w-full rounded-xl"
            data-testid="ai-workspace-run-agent-button"
          >
            {aiRunning ? "Memproses Draft AI..." : "Jalankan Agent"}
          </Button>

          {!workspaceData.ai_settings.enabled ? (
            <div className="rounded-2xl border border-dashed bg-amber-50 p-4 text-sm text-amber-800">
              AI Workspace saat ini dikunci oleh paket. Upgrade ke Professional atau lebih tinggi untuk mengaktifkan agent premium dan kuota AI.
            </div>
          ) : null}

          <div className="rounded-[20px] border bg-slate-50/70 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Output Workspace</p>
                <p className="text-xs text-muted-foreground">Draft AI selalu memerlukan validasi manusia sebelum dipakai.</p>
              </div>
              {aiResult ? <Badge className="rounded-full bg-[hsl(var(--tenant-primary))] text-white">{aiResult.draft_label}</Badge> : null}
            </div>
            <div className="rounded-2xl border bg-white p-4 text-sm leading-7 text-slate-700 min-h-[160px]">
              {aiResult?.answer || "Jalankan agent untuk melihat draf jawaban berbasis dokumen tenant dan citations terkait."}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
        <CardHeader>
          <CardTitle>Citations & Context</CardTitle>
          <CardDescription>Sumber yang dipakai model untuk menyusun jawaban Draft AI.</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <ScrollArea className="h-[680px] pr-3">
            <div className="space-y-3">
              {(aiResult?.citations || []).map((citation) => (
                <div key={citation.chunk_id} className="rounded-2xl border bg-slate-50/70 p-4">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-900">{citation.title}</p>
                    <Badge variant="outline" className="rounded-full">Score {citation.score}</Badge>
                  </div>
                  <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{citation.document_class || "document"}</p>
                  <p className="mt-3 text-sm leading-6 text-slate-700">{citation.snippet}</p>
                </div>
              ))}
              {!aiResult?.citations?.length ? <EmptyState title="Belum ada citation" description="Citation akan tampil di sini setelah agent dijalankan." /> : null}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );

  const renderAdminPage = () => (
    <Tabs defaultValue="branding" className="space-y-6">
      <TabsList className="rounded-2xl border bg-white p-1">
        <TabsTrigger value="branding">Branding</TabsTrigger>
        <TabsTrigger value="usage">Plan & Usage</TabsTrigger>
        <TabsTrigger value="users">Users & Roles</TabsTrigger>
        <TabsTrigger value="settings">AI & SSO</TabsTrigger>
      </TabsList>
      <TabsContent value="branding">
        <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <ComposerCard
            title="White-label Branding"
            description="Perbarui warna tenant, headline login, dan copy export agar workspace tampak sesuai institusi Anda."
            testId="tenant-branding-form"
            submitLabel="Simpan Branding"
            formState={brandingForm}
            setFormState={setBrandingForm}
            onSubmit={saveBranding}
            fields={[
              { name: "organization_name", label: "Nama Organisasi", placeholder: "Nama tenant", testId: "branding-org-name" },
              { name: "primary_color", label: "Primary Color (HSL)", placeholder: "175 60% 28%", testId: "branding-primary-color" },
              { name: "accent_color", label: "Accent Color (HSL)", placeholder: "42 65% 52%", testId: "branding-accent-color" },
              { name: "login_headline", label: "Headline Login", type: "textarea", span: 2, placeholder: "Copy brand untuk halaman login", testId: "branding-login-headline" },
              { name: "report_header", label: "Header Laporan", placeholder: "Header export PDF", testId: "branding-report-header" },
              { name: "report_footer", label: "Footer Laporan", placeholder: "Footer export PDF", testId: "branding-report-footer" },
            ]}
          />
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <div className="tenant-band rounded-t-[20px]" style={brandAwareStyle(brandingForm)} />
            <CardHeader>
              <CardTitle>Brand Preview</CardTitle>
              <CardDescription>Pratinjau header workspace dan login copy setelah white-label diubah.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              <div className="rounded-[24px] border bg-slate-50/70 p-6" style={brandAwareStyle(brandingForm)}>
                <div className="mb-8 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(var(--tenant-primary))] text-sm font-bold text-white">{(brandingForm.organization_name || tenant?.name || "TN").slice(0, 2).toUpperCase()}</div>
                    <div>
                      <p className="font-semibold text-slate-900">{brandingForm.organization_name || tenant?.name}</p>
                      <p className="text-sm text-muted-foreground">{tenant?.industry}</p>
                    </div>
                  </div>
                  <Badge className="rounded-full bg-[hsl(var(--tenant-accent))] text-slate-900">{workspaceData.plan.name}</Badge>
                </div>
                <h3 className="text-2xl font-semibold text-slate-950">{brandingForm.login_headline || tenant?.login_headline}</h3>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">Preview ini mengikuti token white-label tenant Anda tanpa mengubah keterbacaan dashboard dan tabel enterprise.</p>
                <Button className="mt-6 rounded-xl bg-[hsl(var(--tenant-primary))] text-white" data-testid="tenant-branding-save-button" onClick={saveBranding}>Simpan Branding</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>
      <TabsContent value="usage">
        <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <CardTitle>Plan & Usage</CardTitle>
              <CardDescription>Counter pemakaian untuk seat, cabang, storage, AI, dan laporan bulanan.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              <div className="rounded-2xl border bg-slate-50/70 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{workspaceData.plan.name}</p>
                    <p className="mt-1 text-sm text-muted-foreground">Renewal {formatDate(workspaceData.usage.renewal_date)}</p>
                  </div>
                  <StatusBadge value={workspaceData.usage.billing_status} />
                </div>
              </div>
              {Object.entries(workspaceData.usage.counters).map(([key, item]) => (
                <div key={key} className="rounded-2xl border bg-white p-4">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-slate-900">{key.replaceAll("_", " ")}</p>
                    <span className="text-sm text-muted-foreground">{item.used} / {item.limit}</span>
                  </div>
                  <Progress value={Math.min(item.percent, 100)} className="h-2.5 bg-slate-200" />
                </div>
              ))}
            </CardContent>
          </Card>
          <div className="space-y-6">
            {workspaceData.usage.warnings.length ? workspaceData.usage.warnings.map((warning) => (
              <Card key={warning.metric} className="rounded-[20px] border border-amber-200 bg-amber-50 shadow-none">
                <CardContent className="flex items-start gap-4 p-6">
                  <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
                  <div>
                    <p className="font-semibold text-amber-900">Upgrade prompt</p>
                    <p className="mt-1 text-sm text-amber-800">{warning.message} Ajukan upgrade ke plan yang lebih tinggi untuk meningkatkan kapasitas.</p>
                  </div>
                </CardContent>
              </Card>
            )) : <EmptyState title="Pemakaian masih aman" description="Belum ada metrik yang melewati ambang peringatan 80% pada tenant ini." />}
          </div>
        </div>
      </TabsContent>
      <TabsContent value="users">
        <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
          <TableSection
            title="User Management"
            description="Role, status, dan email demo untuk tenant aktif."
            testId="tenant-users-table"
            rows={workspaceData.users}
            columns={[
              { key: "name", label: "Nama" },
              { key: "email", label: "Email" },
              { key: "role_key", label: "Role", render: (row) => <StatusBadge value={row.role_key} /> },
              { key: "status", label: "Status", render: (row) => <StatusBadge value={row.status} /> },
            ]}
            emptyMessage="Belum ada user tenant."
          />
          <TableSection
            title="Struktur Organisasi"
            description="Subsidiary, business unit, dan branch yang bisa dipakai untuk filter dashboard/workflow."
            testId="tenant-structures-table"
            rows={workspaceData.structures}
            columns={[
              { key: "name", label: "Nama Struktur" },
              { key: "structure_type", label: "Type", render: (row) => <StatusBadge value={row.structure_type} /> },
              { key: "status", label: "Status", render: (row) => <StatusBadge value={row.status} /> },
            ]}
            emptyMessage="Belum ada struktur organisasi."
          />
        </div>
      </TabsContent>
      <TabsContent value="settings">
        <div className="grid gap-6 xl:grid-cols-2">
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <CardTitle>AI Settings</CardTitle>
              <CardDescription>Kontrol AI per tenant berdasarkan paket dan kuota yang tersedia.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              <div className="rounded-2xl border bg-slate-50/70 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-slate-900">AI Enabled</p>
                  <StatusBadge value={workspaceData.ai_settings.enabled ? "active" : "locked"} />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">Kuota bulanan: {workspaceData.ai_settings.used_this_month} / {workspaceData.ai_settings.monthly_quota}</p>
              </div>
              <div className="space-y-4 rounded-2xl border bg-white p-4">
                <FormField field={{ name: "prompt_retention", label: "Prompt Retention", testId: "ai-prompt-retention-input", placeholder: "180 hari" }} value={aiSettingsForm.prompt_retention} onChange={(name, value) => setAiSettingsForm((current) => ({ ...current, [name]: value }))} />
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">AI Export</label>
                  <Select value={String(aiSettingsForm.export_enabled)} onValueChange={(value) => setAiSettingsForm((current) => ({ ...current, export_enabled: value === "true" }))}>
                    <SelectTrigger className="bg-white" data-testid="ai-export-enabled-select">
                      <SelectValue placeholder="Pilih status export" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Enabled</SelectItem>
                      <SelectItem value="false">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={saveAISettings} className="w-full rounded-xl" disabled={!workspaceData.ai_settings.enabled} data-testid="tenant-ai-settings-save-button">
                  Simpan AI Settings
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
            <CardHeader>
              <CardTitle>SSO-ready Configuration</CardTitle>
              <CardDescription>Placeholder enterprise untuk OIDC/SAML, custom domain, dan tenant-level API keys.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              <div className="rounded-2xl border border-dashed bg-slate-50/70 p-4 text-sm text-muted-foreground">
                SSO provider belum diaktifkan pada v1 demo ini. Arsitektur sudah disiapkan, termasuk role mapping, domain readiness, dan audit trail untuk event login.
              </div>
              <Button variant="outline" className="rounded-xl" disabled>
                Aktifkan SSO (placeholder)
              </Button>
            </CardContent>
          </Card>
        </div>
      </TabsContent>
    </Tabs>
  );

  const renderReportsPage = () => (
    <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
      <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <CardTitle>Reporting Center</CardTitle>
              <CardDescription>Arsip draf, executive pack Draft AI, dan workflow maker-checker untuk report formal.</CardDescription>
            </div>
            <Button className="rounded-xl" onClick={generateExecutiveReport} data-testid="reports-generate-executive-pack">
              Generate Executive Pack
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          {workspaceData.reports.map((report) => (
            <div key={report.id} className="rounded-2xl border bg-slate-50/70 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-slate-900">{report.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Due {formatDate(report.due_date)}</p>
                </div>
                <StatusBadge value={report.status} />
              </div>
              <p className="mt-3 text-sm leading-7 text-muted-foreground">{report.narrative || "Draft report siap ditindaklanjuti dengan maker-checker dan export."}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-xl" onClick={() => reportWorkflowAction(report.id, "submit")}>Submit</Button>
                <Button size="sm" className="rounded-xl" onClick={() => reportWorkflowAction(report.id, "approve")}>Approve</Button>
                <Button size="sm" variant="outline" className="rounded-xl" onClick={() => exportReport(report.id, report.title)} data-testid={`reports-export-${report.id}`}>Export TXT</Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
        <CardHeader>
          <CardTitle>Phase 3 Commercial Controls</CardTitle>
          <CardDescription>Prompt komersial untuk add-on AI reporting, analytics lanjutan, dan paket Enterprise Plus.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Paket saat ini mendukung {workspaceData.usage.counters.reports.used} dari {workspaceData.usage.counters.reports.limit} laporan/bulan. Ajukan upgrade untuk executive pack automation, citation appendix, dan export enterprise-ready.
          </div>
          <div className="rounded-2xl border bg-slate-50/70 p-4 text-sm text-muted-foreground">
            Payment gateway untuk billing upgrade masih placeholder, tetapi entitlements, usage counters, dan workflow reporting sudah ditingkatkan pada fase ini.
          </div>
          <div className="rounded-2xl border bg-white p-4 text-sm text-muted-foreground">
            Maker-checker tersedia pada opini dan report untuk approval flows yang lebih governance-ready.
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderNotificationsPage = () => (
    <div className="space-y-4">
      {workspaceData.notifications.map((notification) => (
        <Card key={notification.id} className="rounded-[20px] border bg-white shadow-[var(--shadow-sm)]">
          <CardContent className="flex items-start justify-between gap-4 p-6">
            <div>
              <p className="font-semibold text-slate-900">{notification.title}</p>
              <p className="mt-2 text-sm leading-7 text-muted-foreground">{notification.message}</p>
            </div>
            <StatusBadge value={notification.severity} />
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderTasksPage = () => (
    <TableSection
      title="Tasks & Calendar"
      description="Kompilasi action item dari rapat dan remediasi temuan untuk sekretariat dan owner terkait."
      testId="tasks-table"
      rows={workspaceData.tasks}
      columns={[
        { key: "title", label: "Task" },
        { key: "owner", label: "Owner" },
        { key: "due_date", label: "Due Date", render: (row) => formatDate(row.due_date) },
        { key: "status", label: "Status", render: (row) => <StatusBadge value={row.status} /> },
        { key: "source", label: "Source", render: (row) => <StatusBadge value={row.source} /> },
      ]}
      emptyMessage="Belum ada tugas aktif."
    />
  );

  const renderKnowledgePage = () => (
    <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      <ComposerCard
        title="Knowledge Object Baru"
        description="Tambahkan policy, template, atau memo internal yang ingin dipakai untuk pencarian dan AI grounding tenant."
        testId="knowledge-create-form"
        submitLabel="Simpan Knowledge"
        formState={knowledgeForm}
        setFormState={setKnowledgeForm}
        onSubmit={() => createRecord("knowledge", { ...knowledgeForm, tags: knowledgeForm.tags.split(",").map((item) => item.trim()).filter(Boolean) }, () => setKnowledgeForm({ title: "", category: "internal_policy", content: "", tags: "" }))}
        fields={[
          { name: "title", label: "Judul", placeholder: "Template memo temuan", testId: "knowledge-title-input" },
          { name: "category", label: "Kategori", type: "select", testId: "knowledge-category-select", options: [
            { value: "internal_policy", label: "Internal Policy" },
            { value: "template", label: "Template" },
            { value: "memo", label: "Memo" },
          ] },
          { name: "content", label: "Konten", type: "textarea", span: 2, placeholder: "Masukkan konten knowledge", testId: "knowledge-content-input" },
          { name: "tags", label: "Tags", placeholder: "fatwa, template", span: 2, testId: "knowledge-tags-input" },
        ]}
      />
      <Tabs defaultValue="private" className="space-y-4">
        <TabsList className="rounded-2xl border bg-white p-1">
          <TabsTrigger value="private">Tenant Private</TabsTrigger>
          <TabsTrigger value="public">Public Library</TabsTrigger>
        </TabsList>
        <TabsContent value="private">
          <TableSection
            title="Tenant Knowledge"
            description="Konten private milik tenant yang siap dipakai pada search dan AI workspace."
            testId="knowledge-private-table"
            rows={workspaceData.knowledge_private}
            columns={[
              { key: "title", label: "Judul" },
              { key: "category", label: "Kategori", render: (row) => <StatusBadge value={row.category} /> },
              { key: "content", label: "Konten", render: (row) => <p className="max-w-[520px] text-sm text-muted-foreground">{row.content}</p> },
            ]}
            emptyMessage="Belum ada knowledge tenant."
          />
        </TabsContent>
        <TabsContent value="public">
          <TableSection
            title="Shared Public References"
            description="Referensi publik yang dibagikan platform untuk membantu bootstrap knowledge tenant."
            testId="knowledge-public-table"
            rows={workspaceData.knowledge_public}
            columns={[
              { key: "title", label: "Judul" },
              { key: "category", label: "Kategori", render: (row) => <StatusBadge value={row.category} /> },
              { key: "content", label: "Konten", render: (row) => <p className="max-w-[520px] text-sm text-muted-foreground">{row.content}</p> },
            ]}
            emptyMessage="Belum ada library publik."
          />
        </TabsContent>
      </Tabs>
    </div>
  );

  const renderAuditPage = () => (
    <TableSection
      title="Audit Trail & Governance Logs"
      description="Audit log untuk dokumen, workflow, AI, dan perubahan konfigurasi tenant."
      testId="tenant-audit-table"
      rows={workspaceData.audit_logs}
      columns={[
        { key: "created_at", label: "Waktu", render: (row) => formatDate(row.created_at) },
        { key: "actor_name", label: "Actor" },
        { key: "module", label: "Module", render: (row) => <StatusBadge value={row.module} /> },
        { key: "action", label: "Action", render: (row) => <StatusBadge value={row.action} /> },
        { key: "detail", label: "Detail" },
      ]}
      emptyMessage="Belum ada audit log untuk tenant ini."
    />
  );

  const tenantRecordPages = {
    submissions: {
      title: "Submission Intake & Workflow",
      description: "Kelola pengajuan produk, policy review, dan permintaan klarifikasi secara tenant-aware.",
      rows: workspaceData?.submissions || [],
      formState: submissionForm,
      setFormState: setSubmissionForm,
      onSubmit: () => createRecord("submissions", submissionForm, () => setSubmissionForm({ title: "", request_type: "new_product", requester: "", description: "", business_unit: "Pembiayaan Konsumer", branch: "Cabang Jakarta", due_date: "" })),
      testId: "submissions-composer",
      submitLabel: "Buat Submission",
      fields: [
        { name: "title", label: "Judul Submission", placeholder: "Produk Murabahah Mikro", testId: "submission-title-input" },
        { name: "request_type", label: "Tipe Workflow", type: "select", testId: "submission-type-select", options: [
          { value: "new_product", label: "New Product" },
          { value: "product_change", label: "Product Change" },
          { value: "policy_review", label: "Policy Review" },
        ] },
        { name: "requester", label: "Requester", placeholder: "Divisi Produk", testId: "submission-requester-input" },
        { name: "business_unit", label: "Business Unit", placeholder: "Pembiayaan Konsumer", testId: "submission-business-unit-input" },
        { name: "branch", label: "Branch", placeholder: "Cabang Jakarta", testId: "submission-branch-input" },
        { name: "due_date", label: "Due Date", type: "date", testId: "submission-due-date-input" },
        { name: "description", label: "Deskripsi", type: "textarea", span: 2, placeholder: "Ringkas latar belakang dan kebutuhan DPS", testId: "submission-description-input" },
      ],
      columns: [
        { key: "title", label: "Submission", render: (row) => <div><p className="font-medium text-slate-900">{row.title}</p><p className="text-xs text-muted-foreground">{row.business_unit} · {row.branch}</p></div> },
        { key: "request_type", label: "Type", render: (row) => <StatusBadge value={row.request_type} /> },
        { key: "status", label: "Status", render: (row) => <StatusBadge value={row.status} /> },
        { key: "due_date", label: "Due Date", render: (row) => formatDate(row.due_date) },
      ],
    },
    opinions: {
      title: "Sharia Opinion Management",
      description: "Draft opini formal dengan linkage ke submission dan kondisi implementasi.",
      rows: workspaceData?.opinions || [],
      formState: opinionForm,
      setFormState: setOpinionForm,
      onSubmit: () => createRecord("opinions", opinionForm, () => setOpinionForm({ title: "", issue_type: "new_product", background: "", final_stance: "Draft", conditions: "" })),
      testId: "opinions-composer",
      submitLabel: "Buat Draft Opini",
      fields: [
        { name: "title", label: "Judul Opini", placeholder: "Opini Syariah Produk Baru", testId: "opinion-title-input" },
        { name: "issue_type", label: "Issue Type", type: "select", testId: "opinion-issue-type-select", options: [
          { value: "new_product", label: "New Product" },
          { value: "policy_review", label: "Policy Review" },
          { value: "special_transaction", label: "Special Transaction" },
        ] },
        { name: "final_stance", label: "Final Stance", type: "select", testId: "opinion-final-stance-select", options: [
          { value: "Draft", label: "Draft" },
          { value: "Approved with Conditions", label: "Approved with Conditions" },
          { value: "Rejected", label: "Rejected" },
        ] },
        { name: "background", label: "Background", type: "textarea", span: 2, placeholder: "Latar belakang analisis dan basis fatwa/regulasi", testId: "opinion-background-input" },
        { name: "conditions", label: "Conditions", type: "textarea", span: 2, placeholder: "Kondisi implementasi dan catatan DPS", testId: "opinion-conditions-input" },
      ],
      columns: [
        { key: "title", label: "Opini" },
        { key: "issue_type", label: "Issue Type", render: (row) => <StatusBadge value={row.issue_type} /> },
        { key: "final_stance", label: "Stance" },
        { key: "status", label: "Status", render: (row) => <StatusBadge value={row.status} /> },
        {
          key: "actions",
          label: "Workflow",
          render: (row) => (
            <div className="flex flex-wrap gap-2">
              {row.status === "draft" ? <Button size="sm" variant="outline" onClick={() => opinionWorkflowAction(row.id, "submit")}>Submit</Button> : null}
              {row.status !== "approved with conditions" ? <Button size="sm" onClick={() => opinionWorkflowAction(row.id, "approve")}>Approve</Button> : null}
            </div>
          ),
        },
      ],
    },
    findings: {
      title: "Findings Command Center",
      description: "War room temuan, severity, owner, dan target remediasi yang bisa dipantau boardroom-style.",
      rows: workspaceData?.findings || [],
      formState: findingForm,
      setFormState: setFindingForm,
      onSubmit: () => createRecord("findings", findingForm, () => setFindingForm({ title: "", source: "Internal Audit", category: "documentation", severity: "High", owner: "", description: "", target_date: "" })),
      testId: "findings-composer",
      submitLabel: "Catat Temuan",
      fields: [
        { name: "title", label: "Judul Temuan", placeholder: "Temuan terkait dokumen atau proses", testId: "finding-title-input" },
        { name: "source", label: "Source", placeholder: "Internal Audit", testId: "finding-source-input" },
        { name: "category", label: "Category", placeholder: "documentation", testId: "finding-category-input" },
        { name: "severity", label: "Severity", type: "select", testId: "finding-severity-select", options: [
          { value: "High", label: "High" },
          { value: "Medium", label: "Medium" },
          { value: "Low", label: "Low" },
        ] },
        { name: "owner", label: "Owner", placeholder: "Nama owner remediasi", testId: "finding-owner-input" },
        { name: "target_date", label: "Target Date", type: "date", testId: "finding-target-date-input" },
        { name: "description", label: "Deskripsi", type: "textarea", span: 2, placeholder: "Jelaskan dampak, akar masalah, dan konteks temuan", testId: "finding-description-input" },
      ],
      columns: [
        { key: "title", label: "Temuan", render: (row) => <div><p className="font-medium text-slate-900">{row.title}</p><p className="text-xs text-muted-foreground">{row.source}</p></div> },
        { key: "severity", label: "Severity", render: (row) => <StatusBadge value={row.severity} /> },
        { key: "owner", label: "Owner" },
        { key: "status", label: "Status", render: (row) => <StatusBadge value={row.status} /> },
      ],
    },
    meetings: {
      title: "Meeting, Decisions & Action System",
      description: "Jadwalkan rapat DPS, atur agenda, dan dokumentasikan keputusan dengan action tracker.",
      rows: workspaceData?.meetings || [],
      formState: meetingForm,
      setFormState: setMeetingForm,
      onSubmit: () => createRecord("meetings", { ...meetingForm, agenda_items: meetingForm.agenda_text.split("\n").map((item) => item.trim()).filter(Boolean) }, () => setMeetingForm({ title: "", meeting_date: "", location: "Board Room Hybrid", agenda_text: "" })),
      testId: "meetings-composer",
      submitLabel: "Jadwalkan Rapat",
      fields: [
        { name: "title", label: "Judul Rapat", placeholder: "Rapat DPS Bulanan", testId: "meeting-title-input" },
        { name: "meeting_date", label: "Tanggal Rapat", type: "date", testId: "meeting-date-input" },
        { name: "location", label: "Lokasi", placeholder: "Board Room Hybrid", testId: "meeting-location-input" },
        { name: "agenda_text", label: "Agenda (pisahkan per baris)", type: "textarea", span: 2, placeholder: "Agenda 1\nAgenda 2", testId: "meeting-agenda-input" },
      ],
      columns: [
        { key: "title", label: "Rapat" },
        { key: "meeting_date", label: "Tanggal", render: (row) => formatDate(row.meeting_date) },
        { key: "location", label: "Lokasi" },
        { key: "status", label: "Status", render: (row) => <StatusBadge value={row.status} /> },
      ],
    },
  };

  const renderTenantRecordPage = (pageKey) => {
    const config = tenantRecordPages[pageKey];
    return (
      <div className="grid gap-6 xl:grid-cols-[0.92fr_1.08fr]">
        <ComposerCard
          title={config.title}
          description={config.description}
          testId={config.testId}
          submitLabel={config.submitLabel}
          formState={config.formState}
          setFormState={config.setFormState}
          onSubmit={config.onSubmit}
          fields={config.fields}
        />
        <TableSection
          title={`${config.title} — Register`}
          description="List utama dengan status, metadata, dan detail paling penting untuk eksekutif maupun sekretariat."
          testId={`${pageKey}-table`}
          rows={config.rows}
          columns={config.columns}
          emptyMessage={`Belum ada data untuk modul ${config.title.toLowerCase()}.`}
        />
      </div>
    );
  };

  const renderTenantPage = () => {
    switch (currentPage) {
      case "dashboard":
        return renderTenantDashboard();
      case "submissions":
      case "opinions":
      case "findings":
      case "meetings":
        return renderTenantRecordPage(currentPage);
      case "reviews":
        return renderReviews();
      case "documents":
        return renderDocuments();
      case "knowledge":
        return renderKnowledgePage();
      case "reports":
        return renderReportsPage();
      case "tasks":
        return renderTasksPage();
      case "ai":
        return renderAiWorkspace();
      case "notifications":
        return renderNotificationsPage();
      case "audit":
        return renderAuditPage();
      case "admin":
        return renderAdminPage();
      default:
        return renderTenantDashboard();
    }
  };

  const renderSuperAdminPage = () => {
    switch (currentPage) {
      case "platform":
        return renderSuperAdminOverview();
      case "crm":
        return renderSuperAdminCrm();
      case "tenants":
        return renderTenantsPage();
      case "plans":
        return renderPlansPage();
      case "library":
        return renderPublicLibraryPage();
      case "audit":
        return renderPlatformAuditPage();
      default:
        return renderSuperAdminOverview();
    }
  };

  if (isCrmPortalRoute && (crmPortalAuthenticated || isCrmAdmin || isSuperAdmin)) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Toaster richColors position="top-right" />
        <div className="tenant-band" />
        <div className="grid min-h-screen lg:grid-cols-[280px_1fr]">
          <aside className="border-r bg-[hsl(var(--sidebar))] px-5 py-6">
            <div className="mb-8 flex items-center gap-3 px-2">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-sm font-bold text-white">CRM</div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">admin-crm.dps-360.com</p>
                <p className="text-sm font-semibold text-slate-900">DPS360 Sales CRM</p>
              </div>
            </div>
            <div className="mb-6 rounded-[24px] border bg-white p-4">
              <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">CRM Admin Session</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{crmPortalUser?.display_name || workspace?.session?.display_name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{crmPortalUser?.role_label || workspace?.session?.role_label}</p>
            </div>
            <nav className="space-y-1.5">
              <button type="button" onClick={() => setCurrentPage('crm')} className={cn("dps-sidebar-item flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium", currentPage === 'crm' ? "bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active))]" : "text-slate-700 hover:bg-white")} data-testid="sidebar-nav-crm-portal">
                <Target className="h-4 w-4" />
                <span>CRM Dashboard</span>
              </button>
            </nav>
            <div className="mt-8 rounded-[24px] border bg-white p-4 text-sm text-muted-foreground">
              Portal ini dipisahkan secara logical dari website utama dps-360.com dan khusus untuk operasional penjualan / investor / white-label.
            </div>
          </aside>
          <main className="px-4 py-6 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-center justify-between gap-4 rounded-[24px] border bg-white p-6 shadow-[var(--shadow-sm)]">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Portal CRM Terpisah</p>
                <h1 className="text-2xl font-semibold text-slate-950">Superadmin-CRM</h1>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="rounded-xl" onClick={() => window.location.href = '/'}>Website Utama</Button>
                <Button variant="outline" className="rounded-xl" onClick={logout}>Keluar</Button>
              </div>
            </div>
            {renderSuperAdminCrm()}
          </main>
        </div>
      </div>
    );
  }


  return (
    <div className="dps-app-shell min-h-screen bg-[hsl(var(--background))]">
      <Toaster richColors position="top-right" />
      <div className="tenant-band" style={brandAwareStyle(tenant)} />
      <div className="grid min-h-[calc(100vh-10px)] lg:grid-cols-[280px_1fr]">
        <aside className="border-r bg-[hsl(var(--sidebar))] px-4 py-6 lg:px-5">
          <div className="mb-8 flex items-center gap-3 px-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[hsl(var(--tenant-primary))] text-sm font-bold text-white">
              {isSuperAdmin ? "SA" : tenant?.logo_text || tenant?.name?.slice(0, 2) || "DP"}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--tenant-primary))]">{isSuperAdmin ? "Platform Operator" : workspaceData.plan.name}</p>
              <p className="text-sm font-semibold text-slate-900">{isSuperAdmin ? "DPS360 Cloud" : tenant?.name}</p>
            </div>
          </div>

          <div className="mb-6 rounded-[24px] border bg-white p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Active Role</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{workspace.session.role_label}</p>
            <p className="mt-1 text-sm text-muted-foreground">{workspace.session.display_name}</p>
            <p className="mt-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">Session {tokenKind === "bearer" ? `JWT${refreshToken ? " + Refresh" : ""}` : "Demo Session"}</p>
            {workspace.session.impersonated_by ? (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Impersonasi oleh {workspace.session.impersonated_by.display_name}
              </div>
            ) : null}
          </div>

          <nav className="space-y-1.5">
            {navigation.map((item) => {
              const Icon = item.icon || moduleIcons[item.key] || LayoutDashboard;
              const active = currentPage === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setCurrentPage(item.key);
                    setSearchResults(null);
                  }}
                  className={cn(
                    "dps-sidebar-item flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium",
                    active ? "bg-[hsl(var(--sidebar-active-bg))] text-[hsl(var(--sidebar-active))]" : "text-slate-700 hover:bg-white"
                  )}
                  data-testid={`sidebar-nav-${item.key}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="mt-8 rounded-[24px] border bg-white p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Commercial Readiness</p>
            <div className="mt-3 space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-3">
                <span>White-label</span>
                <StatusBadge value={isSuperAdmin ? "active" : workspaceData.plan.features.white_label ? "active" : "locked"} />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span>AI Premium</span>
                <StatusBadge value={isSuperAdmin ? "active" : workspaceData.ai_settings.enabled ? "active" : "locked"} />
              </div>
              <div className="flex items-center justify-between gap-3">
                <span>Auditability</span>
                <StatusBadge value={isSuperAdmin ? "advanced" : workspaceData.plan.features.advanced_audit ? "advanced" : "basic"} />
              </div>
            </div>
          </div>
        </aside>

        <main className="min-h-screen bg-[hsl(var(--background))]">
          <header className="sticky top-0 z-30 border-b bg-[hsl(var(--background))]/95 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/80 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{isSuperAdmin ? "Platform Control Layer" : tenant?.industry}</p>
                <h1 className="text-2xl font-semibold text-slate-950">{navigation.find((item) => item.key === currentPage)?.label || "Dashboard"}</h1>
              </div>
              <div className="flex flex-col gap-3 lg:items-end">
                <div className="relative w-full max-w-[680px] lg:min-w-[540px]">
                  <div className="flex items-center gap-2 rounded-2xl border bg-white px-3 py-2 shadow-[var(--shadow-sm)]">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Cari submissions, opinions, findings, documents, knowledge..."
                      className="h-8 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                      data-testid="topbar-global-search"
                    />
                    <Button variant="outline" size="sm" className="rounded-xl" data-testid="topbar-filter-button">
                      <Filter className="mr-2 h-4 w-4" />
                      Filter
                    </Button>
                  </div>
                  <TopBarSearchResults results={searchResults} onPick={(label) => {
                    const map = {
                      Submissions: "submissions",
                      Opinions: "opinions",
                      Findings: "findings",
                      Meetings: "meetings",
                      Documents: "documents",
                      Knowledge: "knowledge",
                    };
                    setCurrentPage(map[label] || currentPage);
                    setSearchResults(null);
                  }} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {!isSuperAdmin ? (
                    <>
                      <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setCurrentPage("submissions")}>
                        Submission Baru
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setCurrentPage("documents")}>
                        Upload Dokumen
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setCurrentPage("ai")}>
                        AI Workspace
                      </Button>
                    </>
                  ) : null}
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={refreshWorkspace}>
                    Refresh
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={logout} data-testid="logout-button">
                    <LogOut className="mr-2 h-4 w-4" /> Keluar
                  </Button>
                </div>
              </div>
            </div>
          </header>

          <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
            <Card className="dps-gradient-header rounded-[26px] border bg-white shadow-[var(--shadow-sm)]">
              <CardContent className="flex flex-col gap-4 p-6 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[hsl(var(--tenant-primary))]">{isSuperAdmin ? "Commercial SaaS Operator View" : "Tenant-aware governance workspace"}</p>
                  <h2 className="mt-2 text-2xl font-semibold text-slate-950">{isSuperAdmin ? "Investor-demo-ready platform control center" : `Executive-grade command center untuk ${tenant?.name}`}</h2>
                  <p className="mt-2 max-w-3xl text-sm leading-7 text-muted-foreground">{isSuperAdmin ? "Kelola tenant, distribusi paket, usage analytics, public references, dan audit platform dalam satu control layer." : tenant?.login_headline}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border bg-white/90 p-4">
                    <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Workspace</p>
                    <p className="mt-2 font-semibold text-slate-900">{isSuperAdmin ? "Platform Operator" : workspaceData.plan.name}</p>
                  </div>
                  <div className="rounded-2xl border bg-white/90 p-4">
                    <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">AI Status</p>
                    <p className="mt-2 font-semibold text-slate-900">{isSuperAdmin ? "Cross-tenant analytics" : workspaceData.ai_settings.enabled ? "Enabled" : "Upgrade required"}</p>
                  </div>
                  <div className="rounded-2xl border bg-white/90 p-4">
                    <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Audit</p>
                    <p className="mt-2 font-semibold text-slate-900">{isSuperAdmin ? `${workspaceData.platform_audit.length} events` : `${workspaceData.audit_logs.length} events`}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {isSuperAdmin ? renderSuperAdminPage() : renderTenantPage()}
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
